import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";

// ─────────────────────────────────────────────────────────────
// CONFIG — calls your local proxy server (server.js), not Gemini directly
// This avoids CORS issues and keeps your API key server-side
// ─────────────────────────────────────────────────────────────
// ── API URL — auto-detects prod vs dev ──────────────────────
// In Vercel: set REACT_APP_API_URL to your Railway/Render backend URL
// In dev:    leave unset → falls back to localhost:3005
const API_BASE  = process.env.REACT_APP_API_URL || "http://localhost:3005";
const PROXY_URL = `${API_BASE}/api/gemini`;

// ─────────────────────────────────────────────────────────────
// LOCAL STORAGE HELPERS
// ─────────────────────────────────────────────────────────────
const ls = {
  get: (k, def) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : def; } catch { return def; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

// ═══════════════════════════════════════════════════════════════
// LOCAL SCORING ENGINE — 100% client-side, no API needed
// 5 dimensions: clarity, context, role, constraints, outputFormat
// Overall = weighted average. Used by Analyzer + Playground.
// ═══════════════════════════════════════════════════════════════
const ROLE_KW    = ["you are","act as","pretend","your role","as a","as an","expert","specialist","senior","professional","persona","imagine you"];
const CTX_KW     = ["background","context","given that","situation","scenario","the user","the audience","my ","our ","for a ","for an ","about ","targeting","aimed at"];
const FMT_KW     = ["bullet","numbered list","list","table","markdown","json","xml","paragraph","section","heading","output","respond with","return","provide","write","create","generate","format as","structure"];
const CON_KW     = ["must","should","do not","avoid","limit to","only","no more than","at most","at least","maximum","minimum","don't","never","always","ensure","tone","style","formal","informal","concise","detailed","short","long","brief","max words","word limit","under"];
const QUALITY_KW = ["specifically","exactly","precise","clear","detailed","structured","step by step","step-by-step","comprehensive","thorough","explicit","professional","in-depth"];

function scorePromptLocal(text) {
  const t     = text.trim();
  const lower = t.toLowerCase();
  const words = t.split(/\s+/).filter(Boolean);
  const len   = words.length;

  const qBonus  = QUALITY_KW.filter(w => lower.includes(w)).length * 5;
  const clarLen = len < 4 ? 5 : len < 8 ? 25 : len < 15 ? 48 : len < 30 ? 65 : len < 50 ? 76 : 84;
  const clarity = Math.min(100, clarLen + qBonus);

  const ctxHits = CTX_KW.filter(w => lower.includes(w)).length;
  const context = Math.min(100, 12 + ctxHits * 16 + (len > 25 ? 8 : 0));

  const roleHits = ROLE_KW.filter(w => lower.includes(w)).length;
  const role     = Math.min(100, roleHits > 0 ? 32 + roleHits * 22 : 8);

  const conHits     = CON_KW.filter(w => lower.includes(w)).length;
  const constraints = Math.min(100, 6 + conHits * 16);

  const fmtHits     = FMT_KW.filter(w => lower.includes(w)).length;
  const outputFormat = Math.min(100, 8 + fmtHits * 18);

  const overall = Math.round(
    clarity * 0.25 + context * 0.20 + role * 0.20 +
    constraints * 0.15 + outputFormat * 0.20
  );

  const dimFeedback = {
    clarity:      clarity < 50  ? "Too vague — add specific details and quality modifiers like 'step-by-step'."
                : clarity < 75  ? "Decent. Add 'comprehensive', 'precise', or 'detailed'."
                :                 "Clear, specific instructions. ✓",
    context:      context < 40  ? "Missing context — who is this for? What's the background situation?"
                : context < 70  ? "Some context present. Add audience, use-case, or scenario details."
                :                 "Good context signals. ✓",
    role:         role < 30     ? "No persona found. Start with 'You are a [specific expert]…'"
                : role < 65     ? "Partial role. Make the persona more explicit and detailed."
                :                 "Role clearly defined. ✓",
    constraints:  constraints < 30 ? "No constraints. Add tone, length, or format rules."
                : constraints < 65  ? "Add more: 'formal tone, max 200 words, no jargon'."
                :                     "Good constraints applied. ✓",
    outputFormat: outputFormat < 30 ? "No output format. Specify: bullet list, JSON, numbered steps, etc."
                : outputFormat < 65 ? "Output format partially defined. Be more explicit."
                :                     "Output format well specified. ✓",
  };

  const problems = [];
  if (len < 5)        problems.push("Prompt is too short — add more detail.");
  if (roleHits === 0) problems.push("Missing role/persona assignment ('You are a…').");
  if (ctxHits < 2)    problems.push("Insufficient context about task, audience, or background.");
  if (fmtHits === 0)  problems.push("No output format specified (list, JSON, steps, etc.).");
  if (conHits === 0)  problems.push("No constraints on tone, length, or style.");

  const suggestions = [
    role < 60         ? "Add 'You are a [specific expert]…' at the start." : null,
    context < 60      ? "Describe the audience or use-case explicitly." : null,
    outputFormat < 60 ? "Specify format: 'Respond as a numbered list'." : null,
    constraints < 60  ? "Add constraints: 'Be concise, max 200 words, professional tone'." : null,
  ].filter(Boolean).slice(0, 3);

  const prefix   = roleHits === 0 ? "You are an expert assistant. " : "";
  const fmtAdd   = fmtHits  === 0 ? " Structure your response with clear headings and bullet points." : "";
  const conAdd   = conHits  === 0 ? " Use a professional, concise tone in under 300 words." : "";
  const ctxAdd   = ctxHits  === 0 ? " Include relevant background where applicable." : "";
  const improved = prefix + t + fmtAdd + conAdd + ctxAdd;

  const changes  = [prefix && "added role", fmtAdd && "added format", conAdd && "added constraints", ctxAdd && "added context"].filter(Boolean);
  const explanation = changes.length
    ? `Improved by ${changes.join(", ")}. Score: ${overall}/100.`
    : `Prompt is solid. Score: ${overall}/100.`;

  return {
    overallScore: overall,
    clarity, context,
    structure:          role,
    specificity:        constraints,
    outputInstructions: outputFormat,
    dimFeedback,
    problems: problems.length ? problems : ["No major issues detected."],
    suggestions,
    improvedPrompt: improved,
    explanation,
    coachQuestions: [
      "What is the exact goal of this prompt?",
      "Who is the target audience or user?",
      "What output format do you need (list, JSON, paragraph)?",
    ],
  };
}

// ─────────────────────────────────────────────────────────────
// AUTH CONTEXT — JWT-based via Node.js server
// ─────────────────────────────────────────────────────────────
const AUTH_URL  = API_BASE;
const AuthCtx   = createContext(null);

function AuthProvider({ children }) {
  const [user,        setUser]    = useState(() => ls.get("pf_user", null));
  const [authLoading, setLoading] = useState(false);
  const [authError,   setError]   = useState("");

  const apiFetch = async (path, body, method="POST") => {
    const token = ls.get("pf_token", null);
    const res = await fetch(AUTH_URL + path, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: "Bearer " + token } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Request failed");
    return data;
  };

  const login = async (username, password) => {
    setLoading(true); setError("");
    try {
      const data = await apiFetch("/auth/login", { username, password });
      ls.set("pf_token", data.token);
      ls.set("pf_user",  data.user);
      setUser(data.user);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setLoading(false); }
  };

  // Step 1 of registration — sends verification email
  const sendCode = async (username, email, password) => {
    setLoading(true); setError("");
    try {
      const data = await apiFetch("/auth/send-code", { username, email, password });
      return data; // { sent:true, dev?: code } — dev exposes code if no SMTP
    } catch (e) { setError(e.message); return null; }
    finally { setLoading(false); }
  };

  // Step 2 — verify code and complete registration
  const verifyAndRegister = async (email, code) => {
    setLoading(true); setError("");
    try {
      const data = await apiFetch("/auth/verify-register", { email, code });
      ls.set("pf_token", data.token);
      ls.set("pf_user",  data.user);
      setUser(data.user);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setLoading(false); }
  };

  const logout = () => {
    ls.set("pf_token", null); ls.set("pf_user", null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, authLoading, authError, setError, login, sendCode, verifyAndRegister, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
const useAuth = () => useContext(AuthCtx);

// ─────────────────────────────────────────────────────────────
// APP CONTEXT — single source of truth, fully wired to localStorage
// ─────────────────────────────────────────────────────────────
const AppCtx = createContext(null);

const DUMMY_BOARD = [
  { name:"PromptWizard", xp:4820, level:25, bestScore:98, missionsCompleted:8,  badge:"🥇" },
  { name:"AICrafter",    xp:3210, level:17, bestScore:95, missionsCompleted:7,  badge:"🥈" },
  { name:"NeuralNinja",  xp:2890, level:15, bestScore:92, missionsCompleted:6,  badge:"🥉" },
  { name:"TokenMaster",  xp:1540, level:9,  bestScore:88, missionsCompleted:5,  badge:"🎖️" },
  { name:"ContextKing",  xp:980,  level:6,  bestScore:84, missionsCompleted:3,  badge:"🎖️" },
];

function AppProvider({ children }) {
  const [xp,           setXP]      = useState(() => ls.get("pf_xp", 0));
  const [allScores,    setScores]  = useState(() => ls.get("pf_scores", []));
  const [history,      setHistory] = useState(() => ls.get("pf_history", []));
  const [badges,       setBadges]  = useState(() => ls.get("pf_badges", []));
  const [missionsDone, setMDone]   = useState(() => ls.get("pf_missions_done", []));
  const [dailyDone,    setDDone]   = useState(() => ls.get("pf_daily_done", {})); // { "date:id": true }
  const [streak,       setStreak]  = useState(() => ls.get("pf_streak", 0));
  const [lastActive,   setLastAct] = useState(() => ls.get("pf_last_active", ""));
  const [board,        setBoard]   = useState(DUMMY_BOARD);
  const [sessionCount, setSCnt]    = useState(0);

  // Derived
  const level    = Math.floor(xp / 200) + 1;
  const xpToNext = 200 - (xp % 200);
  const analyses = history.filter(h => h.type === "analyze").length;
  const bestScore = allScores.length ? Math.max(...allScores) : 0;
  const avgScore  = allScores.length ? Math.round(allScores.reduce((a,b)=>a+b,0)/allScores.length) : 0;

  // Keep "You" on leaderboard
  useEffect(() => {
    setBoard(prev => {
      const filtered = prev.filter(e => e.name !== "You");
      const you = {
        name:"You", xp, level, bestScore,
        missionsCompleted: missionsDone.length,
        badge:"⚡", isMe:true,
      };
      return [...filtered, you].sort((a,b) => b.xp - a.xp);
    });
  }, [xp, level, bestScore, missionsDone.length]);

  // Streak tracking — update on load and on each action
  const checkStreak = useCallback(() => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    if (lastActive === today) return; // already updated today
    const newStreak = lastActive === yesterday ? streak + 1 : 1;
    setStreak(newStreak);
    setLastAct(today);
    ls.set("pf_streak", newStreak);
    ls.set("pf_last_active", today);
  }, [lastActive, streak]);

  const addXP = useCallback((amount) => {
    setXP(prev => {
      const next = prev + amount;
      ls.set("pf_xp", next);
      return next;
    });
    checkStreak();
  }, [checkStreak]);

  const addAnalysis = useCallback((score) => {
    setScores(prev => {
      const next = [...prev, score].slice(-500);
      ls.set("pf_scores", next);
      return next;
    });
    setSCnt(c => c + 1);
  }, []);

  const addHistory = useCallback((entry) => {
    setHistory(prev => {
      const next = [{ ...entry, id: Date.now(), date: new Date().toISOString() }, ...prev].slice(0, 100);
      ls.set("pf_history", next);
      return next;
    });
  }, []);

  const earnBadge = useCallback((badge) => {
    if (!badge) return;
    setBadges(prev => {
      if (prev.find(b => b.id === badge.id)) return prev;
      const next = [...prev, { ...badge, earnedAt: new Date().toISOString() }];
      ls.set("pf_badges", next);
      return next;
    });
  }, []);

  const completeMission = useCallback((id, xpAmount) => {
    setMDone(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      ls.set("pf_missions_done", next);
      return next;
    });
    addXP(xpAmount);
  }, [addXP]);

  const completeDailyMission = useCallback((dailyId, xpAmount) => {
    const today = new Date().toDateString();
    const key   = `${today}:${dailyId}`;
    setDDone(prev => {
      if (prev[key]) return prev;
      const next = { ...prev, [key]: true };
      ls.set("pf_daily_done", next);
      return next;
    });
    addXP(xpAmount);
    // Streak bonus for completing all 3 dailies today
    const today2 = new Date().toDateString();
    const todayDone = Object.keys(dailyDone).filter(k => k.startsWith(today2)).length + 1;
    if (todayDone >= 3) earnBadge(ALL_BADGES.find(b => b.id === "daily3"));
  }, [addXP, earnBadge, dailyDone]);

  const isDailyDone = useCallback((dailyId) => {
    const today = new Date().toDateString();
    return !!dailyDone[`${today}:${dailyId}`];
  }, [dailyDone]);

  return (
    <AppCtx.Provider value={{
      xp, level, xpToNext, analyses, bestScore, avgScore,
      history, badges, board, missionsDone, streak,
      sessionCount,
      addXP, addAnalysis, addHistory, earnBadge,
      completeMission, completeDailyMission, isDailyDone,
    }}>
      {children}
    </AppCtx.Provider>
  );
}
const useApp = () => useContext(AppCtx);



// ─────────────────────────────────────────────────────────────
// GEMINI API  — single helper, retry on 429
// ─────────────────────────────────────────────────────────────
async function callGemini(prompt, maxTokens = 800, retries = 2) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    let res;
    try {
      res = await fetch(PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, maxTokens }),
      });
    } catch (e) {
      if (attempt === retries) {
        throw new Error("Cannot reach proxy server. Make sure server.js is running: open a terminal and run 'node server.js'");
      }
      await new Promise(r => setTimeout(r, 1200));
      continue;
    }

    const data = await res.json().catch(() => ({}));

    if (res.status === 429) {
      if (attempt === retries) throw new Error("Rate limit hit — wait 30 seconds and try again.");
      await new Promise(r => setTimeout(r, 3500 * (attempt + 1)));
      continue;
    }
    if (!res.ok) {
      throw new Error(data?.error || `Server error ${res.status}`);
    }

    const raw = data?.text ?? "";
    if (!raw) throw new Error("Empty response. Try again.");

    const clean = raw.replace(/^```json\s*/i, "").replace(/```\s*$/i, "").trim();
    try { return JSON.parse(clean); }
    catch {
      const match = clean.match(/\{[\s\S]*\}/);
      if (match) { try { return JSON.parse(match[0]); } catch {} }
      throw new Error("Could not parse AI response. Raw: " + raw.slice(0, 200));
    }
  }
}

// ─────────────────────────────────────────────────────────────
// PROMPT ANALYSIS — now purely local. No API needed.
// Coach + Missions still use AI (callGemini).
// ─────────────────────────────────────────────────────────────
function analyzePrompt(prompt) {
  // Synchronous local scoring — instant, no cost, no network
  return scorePromptLocal(prompt);
}


// ─────────────────────────────────────────────────────────────
// MISSION SCORING  (Feature 4)
// ─────────────────────────────────────────────────────────────
async function scoreMission(mission, userPrompt) {
  const p = `Score this prompt training mission. Return ONLY valid JSON, no extra text.
JSON: {"score":74,"xpEarned":35,"passed":true,"feedback":"2 sentence feedback.","strengths":["s1"],"improvements":["i1"]}
Mission: ${mission.title} — ${mission.goal}
Prompt: "${userPrompt.slice(0,300)}"
Pass if score>=60.`;
  return callGemini(p, 400);
}

// ─────────────────────────────────────────────────────────────
// PLAYGROUND COMPARE  (Feature 7)
// ─────────────────────────────────────────────────────────────
async function playgroundAnalyze(prompt) {
  const p = `Analyze this prompt. Return ONLY valid JSON, no extra text.
JSON: {"score":70,"simulatedOutput":"2-3 sentence simulated AI response.","strengths":["s1"],"weaknesses":["w1"],"quickFix":"One sentence fix."}
Prompt: "${prompt.slice(0,300)}"`;
  return callGemini(p, 400);
}

// ─────────────────────────────────────────────────────────────
// VOICE INPUT HOOK
// ─────────────────────────────────────────────────────────────
function useVoice(onResult) {
  const [listening, setListening]   = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const recRef = useRef(null);

  const start = useCallback(() => {
    setVoiceError("");
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setVoiceError("Voice not supported — use Chrome or Edge."); return; }
    try {
      const r = new SR();
      r.lang = "en-US"; r.continuous = false; r.interimResults = false;
      r.onstart = () => setListening(true);
      r.onresult = e => { const t = e.results[0]?.[0]?.transcript || ""; if (t) onResult(t); setListening(false); };
      r.onerror = e => {
        setListening(false);
        if (e.error === "not-allowed") setVoiceError("Mic denied — allow in browser settings.");
        else if (e.error === "no-speech") setVoiceError("No speech detected.");
        else setVoiceError("Voice error: " + e.error);
      };
      r.onend = () => setListening(false);
      recRef.current = r;
      r.start();
    } catch (e) { setVoiceError("Can't start mic: " + e.message); setListening(false); }
  }, [onResult]);

  const stop = useCallback(() => { recRef.current?.stop(); setListening(false); }, []);
  return { listening, voiceError, start, stop };
}

// ─────────────────────────────────────────────────────────────
// MISSIONS DATA  — 50 leveled missions across 5 tiers
// Levels: 1-2 Beginner, 3-4 Intermediate, 5-7 Advanced, 8-9 Expert, 10 Master
// ─────────────────────────────────────────────────────────────
const MISSIONS = [
  // ── LEVEL 1 — Beginner ─────────────────────────────────────
  { id:"m1",  title:"Your First Prompt",         goal:"Write any prompt and get it analyzed. Just start!",                                              xp:30,  diff:"Beginner",     icon:"🌱", category:"Basics",     level:1 },
  { id:"m2",  title:"Add a Role",                goal:"Write a prompt that starts with 'You are a [role]…' to assign an AI persona",                   xp:35,  diff:"Beginner",     icon:"🎭", category:"Basics",     level:1 },
  { id:"m3",  title:"Specify an Audience",       goal:"Write a prompt that explicitly mentions who the output is for (e.g., 'for a 10-year-old')",      xp:35,  diff:"Beginner",     icon:"👥", category:"Basics",     level:1 },
  { id:"m4",  title:"Fix a Broken Prompt",       goal:"Improve this weak prompt: 'write something about dogs'",                                         xp:40,  diff:"Beginner",     icon:"🔧", category:"Repair",     level:1 },
  { id:"m5",  title:"Ask for a List",            goal:"Write a prompt that explicitly asks for a numbered or bullet-point list output",                  xp:30,  diff:"Beginner",     icon:"📋", category:"Formatting", level:1 },

  // ── LEVEL 2 — Beginner+ ────────────────────────────────────
  { id:"m6",  title:"Marketing Copy",            goal:"Create a product description prompt for an AI ad copywriter for a smartphone",                   xp:50,  diff:"Beginner",     icon:"📣", category:"Marketing",  level:2 },
  { id:"m7",  title:"Set a Tone",                goal:"Write a prompt that explicitly states a tone (formal, humorous, empathetic, etc.)",               xp:45,  diff:"Beginner",     icon:"🎵", category:"Style",      level:2 },
  { id:"m8",  title:"Word Limit Constraint",     goal:"Write a prompt that includes a maximum word or character count constraint",                       xp:45,  diff:"Beginner",     icon:"✂️", category:"Constraints",level:2 },
  { id:"m9",  title:"Email Writer",              goal:"Write a prompt that instructs AI to write a professional follow-up email after a job interview",  xp:50,  diff:"Beginner",     icon:"✉️", category:"Writing",    level:2 },
  { id:"m10", title:"Simple Explainer",          goal:"Write a prompt asking AI to explain a concept as if the reader has never heard of it before",     xp:45,  diff:"Beginner",     icon:"💡", category:"Education",  level:2 },

  // ── LEVEL 3 — Intermediate ─────────────────────────────────
  { id:"m11", title:"Coding Assistant",          goal:"Write a prompt that gets AI to write clean, well-commented Python code for a specific task",      xp:70,  diff:"Intermediate", icon:"💻", category:"Technical",  level:3 },
  { id:"m12", title:"Research Summarizer",       goal:"Craft a prompt that produces structured academic research summaries with citations",              xp:75,  diff:"Intermediate", icon:"🔬", category:"Research",   level:3 },
  { id:"m13", title:"Multi-Format Output",       goal:"Write a prompt that requests output in multiple formats: summary + bullets + code",              xp:80,  diff:"Intermediate", icon:"📊", category:"Formatting", level:3 },
  { id:"m14", title:"Context-Rich Prompt",       goal:"Write a prompt with at least 3 distinct context signals: audience, background, and goal",        xp:75,  diff:"Intermediate", icon:"🗺️", category:"Context",    level:3 },
  { id:"m15", title:"Persona Engineering",       goal:"Write a prompt giving AI a highly specific expert persona with field, years of experience, style",xp:70,  diff:"Intermediate", icon:"🧑‍💼", category:"Personas",   level:3 },

  // ── LEVEL 4 — Intermediate+ ───────────────────────────────
  { id:"m16", title:"Blog Post Brief",           goal:"Write a prompt that produces a full 1000-word blog post with title, intro, 3 sections, conclusion",xp:85, diff:"Intermediate", icon:"📝", category:"Writing",    level:4 },
  { id:"m17", title:"SQL Query Generator",       goal:"Write a prompt that tells AI to generate a specific SQL query given a table schema",             xp:85,  diff:"Intermediate", icon:"🗄️", category:"Technical",  level:4 },
  { id:"m18", title:"Social Media Pack",         goal:"Prompt that generates captions for Twitter, LinkedIn, and Instagram from one topic",             xp:80,  diff:"Intermediate", icon:"📱", category:"Marketing",  level:4 },
  { id:"m19", title:"Feedback Request",          goal:"Write a prompt that asks AI to critique a piece of writing and provide constructive feedback",   xp:80,  diff:"Intermediate", icon:"✏️", category:"Review",     level:4 },
  { id:"m20", title:"Constrained Creativity",    goal:"Write a prompt that generates a short story with 5 specific creative constraints",               xp:90,  diff:"Intermediate", icon:"🎨", category:"Creative",   level:4 },

  // ── LEVEL 5 — Advanced ────────────────────────────────────
  { id:"m21", title:"Chain-of-Thought",          goal:"Design a prompt that forces the AI to reason step-by-step before giving a final answer",        xp:100, diff:"Advanced",     icon:"🧠", category:"Advanced",   level:5 },
  { id:"m22", title:"JSON Extractor",            goal:"Write a prompt that extracts structured JSON data from a block of unstructured natural language", xp:100, diff:"Advanced",     icon:"⚙️", category:"Technical",  level:5 },
  { id:"m23", title:"Self-Critique Loop",        goal:"Write a prompt that instructs AI to generate output, then critique it, then improve it",         xp:110, diff:"Advanced",     icon:"🔄", category:"Advanced",   level:5 },
  { id:"m24", title:"Debate Simulator",          goal:"Write a prompt that makes AI argue both sides of a controversial topic with equal force",        xp:105, diff:"Advanced",     icon:"⚖️", category:"Advanced",   level:5 },
  { id:"m25", title:"Persona Dialogue",          goal:"Write a prompt where two named AI personas have a back-and-forth conversation on a topic",       xp:100, diff:"Advanced",     icon:"💬", category:"Creative",   level:5 },

  // ── LEVEL 6 — Advanced+ ──────────────────────────────────
  { id:"m26", title:"Few-Shot Prompting",        goal:"Write a prompt that includes 3 input-output examples to teach the AI a pattern",                xp:115, diff:"Advanced",     icon:"📐", category:"Advanced",   level:6 },
  { id:"m27", title:"Code Reviewer",             goal:"Write a prompt that reviews code for bugs, security issues, and performance in one pass",       xp:115, diff:"Advanced",     icon:"🔍", category:"Technical",  level:6 },
  { id:"m28", title:"Brand Voice Guide",         goal:"Write a prompt that produces an AI brand voice guide for a company with tone, examples, rules",  xp:110, diff:"Advanced",     icon:"🏷️", category:"Marketing",  level:6 },
  { id:"m29", title:"Lesson Plan Creator",       goal:"Write a prompt that generates a full lesson plan with objectives, activities, and assessment",   xp:110, diff:"Advanced",     icon:"📚", category:"Education",  level:6 },
  { id:"m30", title:"Data Analysis Prompt",      goal:"Write a prompt asking AI to analyze a dataset description and suggest 5 hypotheses to test",    xp:120, diff:"Advanced",     icon:"📈", category:"Research",   level:6 },

  // ── LEVEL 7 — Advanced Expert ────────────────────────────
  { id:"m31", title:"Tree of Thought",           goal:"Design a prompt using tree-of-thought reasoning: explore multiple solution paths before concluding",xp:130,diff:"Advanced",   icon:"🌳", category:"Advanced",   level:7 },
  { id:"m32", title:"Adversarial Testing",       goal:"Write a prompt that intentionally tries to find edge cases or failure modes in an AI response",  xp:125, diff:"Advanced",     icon:"🎯", category:"Technical",  level:7 },
  { id:"m33", title:"Legal Document Draft",      goal:"Write a prompt to generate a first-draft NDA clause with specific legal constraints",            xp:125, diff:"Advanced",     icon:"⚖️", category:"Professional",level:7 },
  { id:"m34", title:"API Design Prompt",         goal:"Write a prompt that generates a full REST API design with endpoints, methods, and schemas",      xp:130, diff:"Advanced",     icon:"🔌", category:"Technical",  level:7 },
  { id:"m35", title:"Emotion-Aware Response",    goal:"Write a prompt that detects emotional tone in user input and adjusts response accordingly",      xp:120, diff:"Advanced",     icon:"💙", category:"Advanced",   level:7 },

  // ── LEVEL 8 — Expert ────────────────────────────────────
  { id:"m36", title:"Recursive Summarizer",      goal:"Write a prompt chain: first summarize, then summarize the summary, then extract key insight",   xp:150, diff:"Expert",       icon:"🔁", category:"Expert",     level:8 },
  { id:"m37", title:"Socratic Questioner",       goal:"Design a prompt that makes AI only respond with questions to guide a student to an answer",      xp:145, diff:"Expert",       icon:"❓", category:"Expert",     level:8 },
  { id:"m38", title:"Prompt Injection Defense",  goal:"Write a prompt that includes instructions to resist and flag prompt injection attempts",         xp:150, diff:"Expert",       icon:"🛡️", category:"Security",   level:8 },
  { id:"m39", title:"Multi-Agent Coordinator",   goal:"Design a prompt that describes two AI agents with different roles working on the same problem",  xp:155, diff:"Expert",       icon:"🤝", category:"Expert",     level:8 },
  { id:"m40", title:"Quantitative Analyst",      goal:"Write a prompt that instructs AI to analyze numbers, flag anomalies, and format findings as a report",xp:145,diff:"Expert",  icon:"📉", category:"Research",   level:8 },

  // ── LEVEL 9 — Expert+ ───────────────────────────────────
  { id:"m41", title:"Constitutional Prompt",     goal:"Write a prompt with built-in rules that constrain AI output (like a constitution for the response)",xp:160,diff:"Expert",    icon:"📜", category:"Expert",     level:9 },
  { id:"m42", title:"System Prompt Architect",   goal:"Write a complete system prompt for a customer support AI with persona, limits, and escalation rules",xp:165,diff:"Expert",   icon:"🏗️", category:"Expert",     level:9 },
  { id:"m43", title:"MetaPrompt",                goal:"Write a prompt that instructs AI to generate another optimized prompt for a given task",           xp:160, diff:"Expert",     icon:"🪞", category:"Expert",     level:9 },
  { id:"m44", title:"Simulation Architect",      goal:"Write a prompt that creates a realistic simulation of a specific professional scenario",           xp:155, diff:"Expert",     icon:"🎲", category:"Expert",     level:9 },
  { id:"m45", title:"Red Team Challenger",       goal:"Write a prompt designed to stress-test an AI's safety and ethical reasoning with hard questions",  xp:165, diff:"Expert",     icon:"⚠️", category:"Security",   level:9 },

  // ── LEVEL 10 — Master ──────────────────────────────────
  { id:"m46", title:"Full Prompt System",        goal:"Design a complete prompt ecosystem: system prompt + user prompt + output validator instructions",  xp:200, diff:"Master",      icon:"👑", category:"Master",     level:10 },
  { id:"m47", title:"AI Tutor Architect",        goal:"Build a prompt for an AI tutor that adapts difficulty based on student answers dynamically",       xp:200, diff:"Master",      icon:"🎓", category:"Master",     level:10 },
  { id:"m48", title:"Infinite Improvement Loop", goal:"Write a prompt chain where AI output feeds back as input and improves each iteration",             xp:200, diff:"Master",      icon:"♾️", category:"Master",     level:10 },
  { id:"m49", title:"The Perfect Prompt",        goal:"Write a single prompt that scores 95+ on all 5 dimensions: role, context, format, constraints, clarity",xp:250,diff:"Master", icon:"💎", category:"Master",     level:10 },
  { id:"m50", title:"Forge Master",              goal:"Complete any 20 missions and write a reflection prompt on what makes a great prompt engineer",     xp:300, diff:"Master",      icon:"⚒️", category:"Master",     level:10 },
];

// ─────────────────────────────────────────────────────────────
// DAILY MISSIONS — 7 rotating challenges, new set each day
// ─────────────────────────────────────────────────────────────
const DAILY_POOL = [
  { id:"d1",  title:"Morning Warm-Up",     goal:"Write any prompt scoring above 60",                                   xp:25,  icon:"☀️" },
  { id:"d2",  title:"Role Master",         goal:"Write a prompt with a clear, specific role assignment",               xp:30,  icon:"🎭" },
  { id:"d3",  title:"Format Freak",        goal:"Write a prompt that specifies JSON output format",                    xp:30,  icon:"📐" },
  { id:"d4",  title:"Context King",        goal:"Write a prompt with 3+ context signals (audience, background, goal)", xp:35,  icon:"🗺️" },
  { id:"d5",  title:"Constraint Crush",    goal:"Add at least 2 style/length constraints to a prompt",                xp:30,  icon:"✂️" },
  { id:"d6",  title:"Score 80+",           goal:"Get any single prompt to score 80 or above",                         xp:40,  icon:"💎" },
  { id:"d7",  title:"Chain It",            goal:"Write a prompt that asks for step-by-step reasoning",                 xp:35,  icon:"🔗" },
  { id:"d8",  title:"Fix It Fast",         goal:"Take a bad 3-word prompt and score it above 70",                      xp:35,  icon:"🔧" },
  { id:"d9",  title:"Persona Pro",         goal:"Write a prompt with a persona + a specific audience + a format",      xp:40,  icon:"🧑‍💼" },
  { id:"d10", title:"Score Perfect",       goal:"Get a prompt to score 90 or above",                                   xp:50,  icon:"🏆" },
  { id:"d11", title:"Feedback Loop",       goal:"Use the AI Coach on any prompt",                                      xp:35,  icon:"🤝" },
  { id:"d12", title:"Playground Run",      goal:"Analyze 2+ prompts in the Playground",                                xp:30,  icon:"🧪" },
  { id:"d13", title:"Short Sharp",         goal:"Write a prompt under 20 words that still scores above 55",            xp:35,  icon:"⚡" },
  { id:"d14", title:"The Novelist",        goal:"Write a creative writing prompt with 3 creative constraints",         xp:35,  icon:"✍️" },
];

function getTodaysDailies() {
  // Use date as seed to pick 3 daily missions consistently per day
  const today = new Date().toDateString();
  const seed  = today.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const indices = [];
  let s = seed;
  while (indices.length < 3) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const i = s % DAILY_POOL.length;
    if (!indices.includes(i)) indices.push(i);
  }
  return indices.map(i => ({ ...DAILY_POOL[i], dailyDate: today }));
}


// ─────────────────────────────────────────────────────────────
// BADGES DATA
// ─────────────────────────────────────────────────────────────
const ALL_BADGES = [
  // Analysis
  { id:"first",      icon:"🌱", name:"First Forge",     desc:"Analyzed your first prompt" },
  { id:"scorer80",   icon:"💎", name:"High Scorer",     desc:"Got a score above 80" },
  { id:"scorer90",   icon:"🌟", name:"Elite Scorer",    desc:"Got a score above 90" },
  { id:"streak",     icon:"🔥", name:"On Fire",         desc:"Analyzed 5 prompts in a session" },
  // Coach
  { id:"coach",      icon:"🤝", name:"Coach's Pet",     desc:"Used the AI Coach" },
  // Missions
  { id:"mission3",   icon:"🥉", name:"Mission Bronze",  desc:"Completed 3 missions" },
  { id:"mission10",  icon:"🥇", name:"Mission Gold",    desc:"Completed 10 missions" },
  { id:"allMissions",icon:"👑", name:"Forge Master",    desc:"Completed all 50 missions" },
  // Daily
  { id:"daily3",     icon:"☀️", name:"Daily Crusher",   desc:"Completed all 3 daily missions in one day" },
  { id:"streak7",    icon:"🗓️", name:"7-Day Streak",    desc:"Logged in 7 days in a row" },
  // Progression
  { id:"level5",     icon:"⚡", name:"Level 5",         desc:"Reached level 5" },
  { id:"level10",    icon:"💥", name:"Level 10",        desc:"Reached level 10" },
  // Social
  { id:"share",      icon:"🔗", name:"Spread the Word", desc:"Shared a result" },
  { id:"playground", icon:"🧪", name:"Experimenter",    desc:"Used the Playground" },
];

// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const S = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
:root{
  --bg:#07070f;--bg2:#0c0c17;--bg3:#101020;
  --sur:#14142a;--sur2:#1a1a32;--sur3:#20203c;
  --brd:rgba(255,255,255,0.07);--brd2:rgba(255,255,255,0.13);
  --acc:#7c5cfc;--acc2:#a78bff;--acc3:#c4b5fd;
  --neon:#00f0c0;--neon2:#00c49a;
  --gold:#f59e0b;--rose:#f43f5e;--sky:#38bdf8;
  --ok:#4ade80;--bad:#f87171;--warn:#fb923c;
  --t1:#eeeaff;--t2:#9d94c8;--t3:#5c5585;
  --fd:'Syne',sans-serif;--fb:'DM Sans',sans-serif;
  --r:14px;--r2:9px;--r3:6px;
  --sh:0 8px 32px rgba(0,0,0,0.5);
  --glow:0 0 40px rgba(124,92,252,0.18);
  --gneon:0 0 30px rgba(0,240,192,0.15);
}
html{font-size:16px;scroll-behavior:smooth;}
body{background:var(--bg);color:var(--t1);font-family:var(--fb);line-height:1.6;min-height:100vh;}
button{cursor:pointer;font-family:var(--fb);}
input,textarea,select{font-family:var(--fb);}
a{color:inherit;text-decoration:none;}
::selection{background:rgba(124,92,252,0.35);}
::-webkit-scrollbar{width:6px;height:6px;}
::-webkit-scrollbar-track{background:var(--bg2);}
::-webkit-scrollbar-thumb{background:var(--sur3);border-radius:3px;}

/* LAYOUT */
.app{display:flex;flex-direction:column;min-height:100vh;}
.page{flex:1;}

/* NAV */
.nav{height:64px;display:flex;align-items:center;justify-content:space-between;padding:0 2rem;border-bottom:1px solid var(--brd);position:sticky;top:0;z-index:100;background:rgba(7,7,15,0.92);backdrop-filter:blur(24px);}
.nav-logo{display:flex;align-items:center;gap:0.6rem;cursor:pointer;user-select:none;flex-shrink:0;}
.logo-icon{width:32px;height:32px;background:linear-gradient(135deg,var(--acc),var(--neon));border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:1.05rem;box-shadow:0 0 14px rgba(124,92,252,0.35);}
.logo-txt{font-family:var(--fd);font-weight:800;font-size:1.05rem;letter-spacing:-0.02em;}
.nav-links{display:flex;align-items:center;gap:0.05rem;margin:0 1rem;}
.nl{background:none;border:none;color:var(--t2);font-size:0.84rem;padding:0.42rem 0.82rem;border-radius:var(--r3);transition:all 0.18s;font-weight:500;white-space:nowrap;}
.nl:hover{color:var(--t1);background:var(--sur);}
.nl.act{color:var(--acc3);background:rgba(124,92,252,0.13);font-weight:600;}
.nav-r{display:flex;align-items:center;gap:0.75rem;flex-shrink:0;}
.nav-divider{width:1px;height:20px;background:var(--brd2);margin:0 0.25rem;}

/* XP STATUS BAR */
.xp-status{display:flex;align-items:center;gap:0.6rem;background:rgba(124,92,252,0.08);border:1px solid rgba(124,92,252,0.22);border-radius:24px;padding:0.32rem 0.9rem 0.32rem 0.65rem;cursor:pointer;transition:all 0.2s;user-select:none;}
.xp-status:hover{background:rgba(124,92,252,0.16);border-color:rgba(124,92,252,0.38);}
.xp-status.pop{animation:xpPop 0.4s ease;}
.xp-avatar{width:24px;height:24px;background:linear-gradient(135deg,var(--acc),var(--neon));border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.7rem;font-weight:800;flex-shrink:0;}
.xp-info{display:flex;flex-direction:column;gap:2px;}
.xp-top{display:flex;align-items:center;gap:0.45rem;font-size:0.74rem;font-weight:700;font-family:var(--fd);}
.xp-num{color:var(--acc3);}
.xp-sep{color:var(--t3);font-size:0.6rem;}
.xp-lv{color:var(--neon);}
.xp-bar-wrap{width:64px;height:3px;background:rgba(255,255,255,0.1);border-radius:2px;overflow:hidden;}
.xp-bar-fill{height:100%;background:linear-gradient(90deg,var(--acc),var(--neon));border-radius:2px;transition:width 0.8s ease;}

/* BUTTONS */
.btn{display:inline-flex;align-items:center;justify-content:center;gap:0.45rem;border:none;border-radius:var(--r2);padding:0.6rem 1.25rem;font-size:0.875rem;font-weight:600;transition:all 0.18s;white-space:nowrap;}
.btn:disabled{opacity:0.45;cursor:not-allowed;}
.btn-primary{background:var(--acc);color:#fff;}
.btn-primary:hover:not(:disabled){background:#8b6dfd;box-shadow:0 4px 16px rgba(124,92,252,0.35);}
.btn-neon{background:var(--neon);color:#06141a;font-weight:700;}
.btn-neon:hover:not(:disabled){background:#00d4aa;box-shadow:0 4px 16px rgba(0,240,192,0.3);}
.btn-ghost{background:transparent;border:1px solid var(--brd2);color:var(--t2);}
.btn-ghost:hover:not(:disabled){border-color:rgba(255,255,255,0.25);color:var(--t1);}
.btn-rose{background:rgba(244,63,94,0.15);border:1px solid rgba(244,63,94,0.3);color:var(--rose);}
.btn-rose:hover:not(:disabled){background:rgba(244,63,94,0.25);}
.btn-sm{padding:0.38rem 0.9rem;font-size:0.8rem;}
.btn-lg{padding:0.75rem 1.75rem;font-size:0.95rem;}
.btn-full{width:100%;}

/* INPUTS */
.inp{width:100%;background:var(--bg2);border:1px solid var(--brd);border-radius:var(--r2);color:var(--t1);padding:0.65rem 1rem;font-size:0.9rem;outline:none;transition:border-color 0.2s;}
.inp:focus{border-color:var(--acc);box-shadow:0 0 0 3px rgba(124,92,252,0.1);}
.inp::placeholder{color:var(--t3);}
.pta{width:100%;min-height:160px;background:var(--bg2);border:1px solid var(--brd);border-radius:var(--r);color:var(--t1);padding:1rem 3.5rem 1rem 1.15rem;font-size:0.93rem;font-family:var(--fb);resize:vertical;outline:none;line-height:1.7;transition:border-color 0.2s;}
.pta:focus{border-color:var(--acc);box-shadow:0 0 0 3px rgba(124,92,252,0.1);}
.pta::placeholder{color:var(--t3);}
.il{display:block;font-size:0.78rem;font-weight:600;color:var(--t2);margin-bottom:0.35rem;letter-spacing:0.03em;}
.ig{display:flex;flex-direction:column;}
.piw{position:relative;}
.mic-btn{position:absolute;bottom:0.85rem;right:0.85rem;width:38px;height:38px;border-radius:50%;border:none;background:var(--bg3);color:var(--t2);display:flex;align-items:center;justify-content:center;font-size:1rem;transition:all 0.2s;}
.mic-btn:hover{background:var(--sur2);color:var(--t1);}
.mic-btn.on{background:rgba(244,63,94,0.18);color:var(--rose);animation:pulseMic 1.2s infinite;border:1px solid rgba(244,63,94,0.35);}
.verr{font-size:0.76rem;color:var(--warn);margin-top:0.4rem;padding:0.4rem 0.75rem;background:rgba(251,146,60,0.07);border:1px solid rgba(251,146,60,0.18);border-radius:var(--r3);}

/* CARDS */
.card{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.5rem;}
.card-sm{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r2);padding:1.1rem;}

/* BADGES / TAGS */
.tag{display:inline-flex;align-items:center;gap:0.3rem;padding:0.22rem 0.65rem;border-radius:20px;font-size:0.72rem;font-weight:700;letter-spacing:0.04em;}
.tag-acc{background:rgba(124,92,252,0.14);color:var(--acc3);border:1px solid rgba(124,92,252,0.25);}
.tag-neon{background:rgba(0,240,192,0.1);color:var(--neon);border:1px solid rgba(0,240,192,0.2);}
.tag-gold{background:rgba(245,158,11,0.12);color:var(--gold);border:1px solid rgba(245,158,11,0.25);}
.tag-rose{background:rgba(244,63,94,0.1);color:var(--rose);border:1px solid rgba(244,63,94,0.22);}
.tag-ok{background:rgba(74,222,128,0.1);color:var(--ok);border:1px solid rgba(74,222,128,0.22);}

/* METER */
.meter{display:flex;flex-direction:column;gap:0.28rem;}
.mh{display:flex;justify-content:space-between;align-items:center;}
.ml{font-size:0.78rem;color:var(--t2);font-weight:500;}
.mv{font-size:0.78rem;font-weight:800;font-family:var(--fd);}
.mt{height:5px;background:var(--bg3);border-radius:3px;overflow:hidden;}
.mf{height:100%;border-radius:3px;background:linear-gradient(90deg,var(--acc),var(--acc2));transition:width 1.1s cubic-bezier(0.4,0,0.2,1);}
.mf-ok{background:linear-gradient(90deg,var(--neon2),var(--neon));}
.mf-bad{background:linear-gradient(90deg,var(--rose),var(--warn));}

/* SPINNER */
.sp{width:17px;height:17px;border:2px solid rgba(255,255,255,0.12);border-top-color:var(--acc2);border-radius:50%;animation:spin 0.65s linear infinite;}
.sp-lg{width:42px;height:42px;border-width:3px;}
.sp-neon{border-top-color:var(--neon);}

/* SECTION LABELS */
.slabel{font-size:0.68rem;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:var(--acc2);margin-bottom:0.6rem;}
.stitle{font-size:clamp(1.7rem,4vw,2.6rem);font-weight:800;letter-spacing:-0.03em;font-family:var(--fd);}
.grad{background:linear-gradient(120deg,var(--acc2),var(--neon));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}

/* ERROR / OK MESSAGES */
.errmsg{font-size:0.8rem;color:var(--bad);padding:0.5rem 0.8rem;background:rgba(248,113,113,0.07);border:1px solid rgba(248,113,113,0.2);border-radius:var(--r3);}
.okmsg{font-size:0.8rem;color:var(--ok);padding:0.5rem 0.8rem;background:rgba(74,222,128,0.07);border:1px solid rgba(74,222,128,0.2);border-radius:var(--r3);}

/* ══ HERO — REBUILT ══ */
.hero{min-height:calc(100vh - 64px);display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:6rem 1.5rem 4rem;position:relative;overflow:hidden;}
.hbg{position:absolute;inset:0;pointer-events:none;background:radial-gradient(ellipse 80% 55% at 50% -8%,rgba(124,92,252,0.18),transparent 65%),radial-gradient(ellipse 50% 40% at 88% 72%,rgba(0,240,192,0.1),transparent 60%),radial-gradient(ellipse 38% 38% at 8% 82%,rgba(124,92,252,0.07),transparent 55%);}
.hgrid{position:absolute;inset:0;pointer-events:none;opacity:0.025;background-image:linear-gradient(var(--t1) 1px,transparent 1px),linear-gradient(90deg,var(--t1) 1px,transparent 1px);background-size:60px 60px;}

/* Eyebrow badge */
.hero-badge-wrap{margin-bottom:1.75rem;}
.hero-badge{display:inline-flex;align-items:center;gap:0.55rem;background:rgba(124,92,252,0.09);border:1px solid rgba(124,92,252,0.28);color:var(--acc3);padding:0.38rem 1rem;border-radius:100px;font-size:0.78rem;font-weight:600;letter-spacing:0.03em;}
.hero-badge-dot{width:7px;height:7px;background:var(--neon);border-radius:50%;display:inline-block;animation:glowPulse 2s infinite;}
@keyframes glowPulse{0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(0,240,192,0.5);}50%{opacity:0.7;box-shadow:0 0 0 4px rgba(0,240,192,0);}}

/* Hero headline */
.hero h1{font-family:var(--fd);font-size:clamp(2.8rem,6.5vw,5.8rem);font-weight:800;line-height:1.06;letter-spacing:-0.045em;margin-bottom:0.5rem;}
.hero-eyeline{display:block;}
.hero-sub{font-family:var(--fd);font-size:clamp(1rem,2.2vw,1.4rem);font-weight:500;color:var(--t2);letter-spacing:-0.015em;margin-bottom:1.5rem;}
.hero-desc{font-size:clamp(0.9rem,1.8vw,1.05rem);color:var(--t2);max-width:500px;line-height:1.8;margin:0 auto 2.5rem;font-weight:300;}

/* Hero CTA row */
.hacts{display:flex;gap:0.85rem;flex-wrap:wrap;justify-content:center;margin-bottom:3.5rem;}
.hmission-badge{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;background:var(--gold);color:#1a0e00;border-radius:50%;font-size:0.65rem;font-weight:800;margin-left:0.3rem;flex-shrink:0;}

/* Hero stats row */
.hstats{display:flex;gap:3rem;flex-wrap:wrap;justify-content:center;padding:2rem 0 0;}
.hstat{display:flex;flex-direction:column;align-items:center;gap:0.2rem;}
.hsn{font-family:var(--fd);font-size:2rem;font-weight:800;background:linear-gradient(135deg,var(--t1),var(--acc3));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1;}
.hsl{font-size:0.68rem;color:var(--t3);text-transform:uppercase;letter-spacing:0.07em;}

/* Demo prompt */
.demo-prompt{background:rgba(0,240,192,0.04);border:1px solid rgba(0,240,192,0.18);border-radius:var(--r2);padding:0.75rem 1rem;font-size:0.82rem;color:var(--t2);font-family:monospace;cursor:pointer;transition:all 0.2s;max-width:480px;margin:0 auto 2rem;text-align:left;line-height:1.5;}
.demo-prompt:hover{background:rgba(0,240,192,0.08);border-color:rgba(0,240,192,0.3);color:var(--t1);}
.demo-label{font-size:0.66rem;color:var(--neon);font-family:var(--fd);font-weight:700;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:0.35rem;}

/* ══ TRUSTED BY ══ */
.trusted-sec{border-top:1px solid var(--brd);border-bottom:1px solid var(--brd);background:var(--bg2);padding:2.5rem 2rem;text-align:center;}
.trusted-label{font-size:0.68rem;text-transform:uppercase;letter-spacing:0.12em;color:var(--t3);margin-bottom:1.75rem;}
.trusted-row{display:flex;gap:3.5rem;align-items:center;justify-content:center;flex-wrap:wrap;}
.trusted-name{font-family:var(--fd);font-size:1rem;font-weight:700;color:var(--t3);letter-spacing:-0.02em;transition:color 0.2s;cursor:default;}
.trusted-name:hover{color:var(--t2);}

/* ══ SOCIAL PROOF STRIP ══ */
.proof-strip{display:grid;grid-template-columns:repeat(4,1fr);gap:1px;background:var(--brd);border-top:1px solid var(--brd);}
.proof-cell{background:var(--bg);padding:3.5rem 2.5rem;display:flex;flex-direction:column;gap:0.5rem;}
.proof-num{font-family:var(--fd);font-size:3rem;font-weight:800;letter-spacing:-2px;line-height:1;background:linear-gradient(135deg,var(--acc2),var(--neon));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.proof-desc{font-size:0.85rem;color:var(--t2);font-weight:300;line-height:1.5;}

/* ══ GAME LOOP / HOW IT WORKS ══ */
.how-sec{padding:5rem 1.5rem;max-width:1180px;margin:0 auto;}
.how-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:1px;background:var(--brd);border:1px solid var(--brd);border-radius:var(--r);overflow:hidden;margin-top:2rem;}
.how-step{background:var(--sur);padding:1.75rem 1.5rem;position:relative;transition:background 0.25s;}
.how-step:hover{background:var(--sur2);}
.how-num{font-family:var(--fd);font-size:3rem;font-weight:800;line-height:1;color:rgba(124,92,252,0.15);margin-bottom:0.75rem;}
.how-title{font-family:var(--fd);font-size:0.95rem;font-weight:700;margin-bottom:0.4rem;}
.how-desc{font-size:0.82rem;color:var(--t2);line-height:1.6;}

/* ══ FEATURES MINDMAP SECTION ══ */
.fsec{padding:5rem 1.5rem 6rem;max-width:1180px;margin:0 auto;}
.fsec-hdr{margin-bottom:3.5rem;text-align:center;}
.mindmap-wrap{position:relative;width:100%;}

/* Center hub */
.mm-hub{display:flex;align-items:center;justify-content:center;margin-bottom:2.5rem;}
.mm-hub-inner{background:linear-gradient(135deg,rgba(124,92,252,0.15),rgba(0,240,192,0.08));border:1px solid rgba(124,92,252,0.35);border-radius:18px;padding:1.25rem 2.5rem;text-align:center;position:relative;}
.mm-hub-inner::before{content:'';position:absolute;inset:-1px;border-radius:18px;background:linear-gradient(135deg,rgba(124,92,252,0.4),rgba(0,240,192,0.2));z-index:-1;opacity:0;transition:opacity 0.3s;}
.mm-hub-inner:hover::before{opacity:1;}
.mm-hub-title{font-family:var(--fd);font-size:1.05rem;font-weight:800;margin-bottom:0.2rem;}
.mm-hub-sub{font-size:0.75rem;color:var(--t3);}
.mm-hub-glow{position:absolute;inset:-20px;background:radial-gradient(ellipse,rgba(124,92,252,0.12),transparent 70%);pointer-events:none;border-radius:50%;}

/* Connector lines */
.mm-connectors{position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:0;}

/* Feature node grid */
.mm-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.25rem;position:relative;z-index:1;}
.mm-node{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.75rem 1.5rem;cursor:pointer;transition:all 0.25s;position:relative;overflow:hidden;}
.mm-node::after{content:'';position:absolute;inset:0;opacity:0;background:linear-gradient(135deg,rgba(124,92,252,0.07),rgba(0,240,192,0.03));transition:opacity 0.25s;pointer-events:none;}
.mm-node:hover{transform:translateY(-4px);border-color:rgba(124,92,252,0.32);box-shadow:0 12px 40px rgba(0,0,0,0.4),var(--glow);}
.mm-node:hover::after{opacity:1;}

/* Node accent line on hover */
.mm-node::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,var(--acc),var(--neon));transform:scaleX(0);transform-origin:left;transition:transform 0.3s ease;}
.mm-node:hover::before{transform:scaleX(1);}

/* Node image */
.mm-img-wrap{width:100%;height:140px;border-radius:var(--r2);overflow:hidden;margin-bottom:1.25rem;position:relative;background:var(--bg3);}
.mm-img{width:100%;height:100%;object-fit:cover;transition:transform 0.4s ease;filter:brightness(0.85) saturate(0.9);}
.mm-node:hover .mm-img{transform:scale(1.05);filter:brightness(0.95) saturate(1);}
.mm-img-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(7,7,15,0.7) 0%,transparent 60%);}

/* Node icon badge on image */
.mm-icon-badge{position:absolute;bottom:0.75rem;left:0.75rem;width:36px;height:36px;border-radius:9px;background:rgba(7,7,15,0.8);border:1px solid rgba(255,255,255,0.12);display:flex;align-items:center;justify-content:center;font-size:1.1rem;backdrop-filter:blur(8px);}

/* Node content */
.mm-node-title{font-family:var(--fd);font-size:1rem;font-weight:700;margin-bottom:0.4rem;letter-spacing:-0.01em;}
.mm-node-desc{font-size:0.82rem;color:var(--t2);line-height:1.65;margin-bottom:0.85rem;font-weight:300;}
.mm-node-meta{display:flex;align-items:center;justify-content:space-between;}
.mm-meta-tag{font-size:0.68rem;color:var(--t3);font-weight:500;letter-spacing:0.04em;}
.mm-open-arrow{font-size:0.78rem;color:var(--acc2);font-weight:700;opacity:0;transform:translateX(-6px);transition:all 0.2s;}
.mm-node:hover .mm-open-arrow{opacity:1;transform:translateX(0);}

/* ══ BEFORE / AFTER ══ */
.sample-sec{padding:3rem 1.5rem 5rem;max-width:900px;margin:0 auto;}
.ba-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.25rem;margin-top:1.75rem;}
.ba-card{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.5rem;}
.ba-card.after{border-color:rgba(0,240,192,0.25);background:rgba(0,240,192,0.03);}
.ba-lbl{font-size:0.65rem;text-transform:uppercase;letter-spacing:0.1em;font-family:var(--fd);margin-bottom:0.85rem;font-weight:700;}
.ba-prompt{font-size:0.84rem;color:var(--t2);font-family:monospace;line-height:1.6;margin-bottom:1rem;padding:0.75rem;background:var(--bg2);border-radius:var(--r3);}
.ba-scores{display:flex;flex-direction:column;gap:0.5rem;}

/* ══ WHY DIFFERENT ══ */
.why-sec{padding:5rem 1.5rem 6rem;background:var(--bg2);}
.why-inner{max-width:1100px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center;}
.why-img-col{position:relative;border-radius:var(--r);overflow:hidden;}
.why-img{width:100%;height:380px;object-fit:cover;border-radius:var(--r);filter:brightness(0.7) saturate(0.8);}
.why-img-badge{position:absolute;bottom:1.5rem;left:1.5rem;background:rgba(7,7,15,0.85);border:1px solid var(--brd2);border-radius:var(--r2);padding:0.75rem 1.1rem;backdrop-filter:blur(12px);}
.why-badge-num{font-family:var(--fd);font-size:1.6rem;font-weight:800;background:linear-gradient(135deg,var(--acc2),var(--neon));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1;}
.why-badge-label{font-size:0.7rem;color:var(--t3);margin-top:0.1rem;}
.why-text-col{padding-right:1rem;}
.why-check-list{list-style:none;display:flex;flex-direction:column;gap:1.1rem;margin:1.5rem 0 2rem;}
.why-check-item{display:flex;gap:0.9rem;align-items:flex-start;}
.why-check-dot{width:20px;height:20px;border-radius:50%;background:rgba(0,240,192,0.12);border:1px solid rgba(0,240,192,0.3);display:flex;align-items:center;justify-content:center;font-size:0.65rem;flex-shrink:0;margin-top:0.1rem;color:var(--neon);}
.why-check-text{font-size:0.9rem;color:var(--t2);line-height:1.6;font-weight:300;}

/* ══ TESTIMONIALS ══ */
.testi-sec{padding:5rem 1.5rem 6rem;background:var(--bg);}
.testi-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:1.25rem;max-width:1100px;margin:2.5rem auto 0;}
.testi-card{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:2rem;transition:border-color 0.25s;}
.testi-card:hover{border-color:var(--brd2);}
.testi-stars{color:var(--gold);font-size:0.85rem;letter-spacing:2px;margin-bottom:1rem;}
.testi-quote{font-size:0.875rem;color:var(--t2);line-height:1.8;margin-bottom:1.5rem;font-weight:300;font-style:italic;}
.testi-author{display:flex;align-items:center;gap:0.85rem;}
.testi-av{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-family:var(--fd);font-size:0.75rem;font-weight:800;flex-shrink:0;}
.testi-name{font-size:0.83rem;font-weight:600;}
.testi-role{font-size:0.73rem;color:var(--t3);margin-top:0.1rem;}

/* ══ PRICING ══ */
.pp{max-width:1080px;margin:0 auto;padding:4rem 1.5rem;}
.pgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(275px,1fr));gap:1.4rem;}
.pcard{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:2rem;position:relative;transition:all 0.2s;}
.pcard:hover{transform:translateY(-3px);}
.pcard.pop{border-color:var(--acc);box-shadow:var(--glow);}
.pbadge{position:absolute;top:-11px;left:50%;transform:translateX(-50%);background:linear-gradient(135deg,var(--acc),var(--neon));color:#fff;font-size:0.64rem;font-weight:700;padding:0.18rem 0.9rem;border-radius:20px;text-transform:uppercase;letter-spacing:0.1em;}
.pname{font-family:var(--fd);font-size:1rem;font-weight:700;margin-bottom:0.35rem;}
.pprice{font-family:var(--fd);font-size:2.4rem;font-weight:800;line-height:1;margin-bottom:0.2rem;}
.pprice span{font-size:0.95rem;font-weight:400;color:var(--t3);}
.pdesc{font-size:0.83rem;color:var(--t2);margin-bottom:1.4rem;padding-bottom:1.4rem;border-bottom:1px solid var(--brd);}
.pfl{list-style:none;display:flex;flex-direction:column;gap:0.65rem;margin-bottom:1.5rem;}
.pfi{display:flex;gap:0.55rem;font-size:0.85rem;color:var(--t2);align-items:flex-start;}
.ck{color:var(--neon);flex-shrink:0;}.cx{color:var(--t3);flex-shrink:0;}

/* ══ CTA BAND ══ */
.cta-band{background:var(--bg2);padding:6rem 1.5rem;text-align:center;position:relative;overflow:hidden;}
.cta-band-glow{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:600px;height:300px;background:radial-gradient(ellipse,rgba(124,92,252,0.12),transparent 70%);pointer-events:none;}
.cta-band h2{font-family:var(--fd);font-size:clamp(2rem,5vw,3.5rem);font-weight:800;letter-spacing:-0.04em;line-height:1.1;margin-bottom:1rem;}
.cta-band p{font-size:1rem;color:var(--t2);margin-bottom:2.5rem;max-width:420px;margin-left:auto;margin-right:auto;font-weight:300;}
.cta-btns{display:flex;gap:1rem;justify-content:center;flex-wrap:wrap;}

/* ANALYZER PAGE */
.apage{max-width:920px;margin:0 auto;padding:3rem 1.5rem;}
.rgrid{display:grid;grid-template-columns:260px 1fr;gap:1.4rem;margin-top:2rem;}
.osc{background:linear-gradient(135deg,rgba(124,92,252,0.1),rgba(0,240,192,0.05));border:1px solid rgba(124,92,252,0.22);border-radius:var(--r);padding:1.5rem;text-align:center;}
.osn{font-family:var(--fd);font-size:4.2rem;font-weight:800;line-height:1;background:linear-gradient(135deg,var(--acc2),var(--neon));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:scorePop 0.55s cubic-bezier(0.34,1.56,0.64,1);}
.osl{font-size:0.68rem;color:var(--t3);text-transform:uppercase;letter-spacing:0.1em;margin-top:0.2rem;}
.ssc{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.2rem;display:flex;flex-direction:column;gap:0.85rem;}
.sp-col{display:flex;flex-direction:column;gap:1rem;}
.fpan{display:flex;flex-direction:column;gap:1rem;}
.fcard{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.4rem;}
.fcard h3{font-size:0.65rem;text-transform:uppercase;letter-spacing:0.12em;color:var(--t3);margin-bottom:0.7rem;font-family:var(--fd);}
.ftxt{font-size:0.875rem;line-height:1.7;color:var(--t2);}
.wl{list-style:none;display:flex;flex-direction:column;gap:0.4rem;}
.wi{display:flex;gap:0.5rem;align-items:flex-start;font-size:0.86rem;color:var(--t2);line-height:1.5;}
.wd{width:6px;height:6px;background:var(--warn);border-radius:50%;flex-shrink:0;margin-top:0.45rem;}
.imp{font-size:0.84rem;line-height:1.75;color:var(--neon);font-family:'Courier New',monospace;background:rgba(0,240,192,0.03);border:1px solid rgba(0,240,192,0.14);border-radius:var(--r2);padding:1rem;}
.share-bar{display:flex;align-items:center;gap:0.75rem;padding:0.9rem 1.1rem;background:rgba(124,92,252,0.07);border:1px solid rgba(124,92,252,0.18);border-radius:var(--r2);margin-top:0.75rem;}
.share-txt{font-size:0.82rem;color:var(--t2);flex:1;}

/* COACH */
.coach-box{background:linear-gradient(135deg,rgba(124,92,252,0.07),rgba(0,240,192,0.04));border:1px solid rgba(124,92,252,0.2);border-radius:var(--r);padding:1.5rem;margin-top:1rem;}
.coach-q{display:flex;flex-direction:column;gap:0.9rem;}
.cq-item{display:flex;flex-direction:column;gap:0.3rem;}
.cq-label{font-size:0.83rem;color:var(--acc3);font-weight:600;}

/* MISSIONS PAGE */
.mpage{max-width:900px;margin:0 auto;padding:3rem 1.5rem;}
.mgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:1rem;}
.mc{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.4rem;cursor:pointer;transition:all 0.2s;position:relative;}
.mc:hover{border-color:var(--brd2);transform:translateY(-2px);}
.mc.done{border-color:rgba(74,222,128,0.3);background:rgba(74,222,128,0.03);}
.mci{font-size:2rem;margin-bottom:0.65rem;display:block;}
.mct{font-family:var(--fd);font-weight:700;font-size:0.97rem;margin-bottom:0.4rem;}
.mcg{font-size:0.82rem;color:var(--t2);line-height:1.55;margin-bottom:0.75rem;}
.mcf{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:0.4rem;}
.xpbadge{font-family:var(--fd);font-size:0.75rem;font-weight:800;color:var(--gold);}
.done-check{position:absolute;top:0.75rem;right:0.75rem;width:22px;height:22px;background:var(--ok);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.65rem;color:#06140a;}
.mres{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.5rem;margin-top:1.5rem;animation:fadeUp 0.35s ease;}
.mrscore{font-family:var(--fd);font-size:3.5rem;font-weight:800;background:linear-gradient(135deg,var(--neon),var(--acc2));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.xpgain{color:var(--gold);font-weight:700;font-size:1rem;margin-top:0.2rem;}

/* LEADERBOARD */
.lpage{max-width:720px;margin:0 auto;padding:3rem 1.5rem;}
.lb-row{display:flex;align-items:center;gap:1rem;padding:0.9rem 1.2rem;background:var(--sur);border:1px solid var(--brd);border-radius:var(--r2);margin-bottom:0.55rem;transition:all 0.18s;}
.lb-row.me{border-color:rgba(124,92,252,0.4);background:rgba(124,92,252,0.08);}
.lb-rank{font-family:var(--fd);font-size:1rem;font-weight:800;color:var(--t3);width:28px;flex-shrink:0;text-align:center;}
.lb-badge{font-size:1.2rem;flex-shrink:0;}
.lb-name{flex:1;font-weight:600;font-size:0.9rem;}
.lb-xp{font-family:var(--fd);font-size:0.9rem;font-weight:800;color:var(--acc2);}
.badges-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:0.75rem;margin-top:1.5rem;}
.badge-card{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r2);padding:1rem;text-align:center;}
.badge-card.earned{border-color:rgba(245,158,11,0.35);background:rgba(245,158,11,0.04);}
.badge-card.locked{opacity:0.35;filter:grayscale(1);}
.badge-icon{font-size:2rem;margin-bottom:0.4rem;}
.badge-name{font-family:var(--fd);font-size:0.8rem;font-weight:700;margin-bottom:0.2rem;}
.badge-desc{font-size:0.7rem;color:var(--t3);line-height:1.4;}

/* PLAYGROUND */
.gpage2{max-width:960px;margin:0 auto;padding:3rem 1.5rem;}
.pv-row{display:grid;grid-template-columns:1fr 1fr;gap:1.25rem;margin-top:1.5rem;}
.pv-card{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.3rem;}
.pv-lbl{font-size:0.65rem;text-transform:uppercase;letter-spacing:0.1em;color:var(--t3);font-family:var(--fd);margin-bottom:0.6rem;}
.pv-out{font-size:0.84rem;color:var(--t2);line-height:1.7;font-style:italic;}
.pv-score{font-family:var(--fd);font-size:2.2rem;font-weight:800;}

/* SHARE MODAL */
.modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.75);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1.5rem;}
.modal{background:var(--sur);border:1px solid var(--brd2);border-radius:var(--r);padding:2rem;max-width:520px;width:100%;box-shadow:var(--sh);animation:fadeUp 0.3s ease;}
.share-card{background:linear-gradient(135deg,var(--bg2),var(--sur));border:1px solid rgba(124,92,252,0.3);border-radius:var(--r);padding:1.75rem;text-align:center;}
.share-watermark{font-size:0.7rem;color:var(--t3);margin-top:1rem;letter-spacing:0.06em;}
.share-scores{display:flex;align-items:center;justify-content:center;gap:1.5rem;margin:1rem 0;}
.share-score-num{font-family:var(--fd);font-size:3rem;font-weight:800;line-height:1;}
.share-arrow{font-size:1.5rem;color:var(--neon);}

/* DASHBOARD/HISTORY */
.dpage{max-width:1080px;margin:0 auto;padding:3rem 1.5rem;}
.dgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:1rem;margin-bottom:1.75rem;}
.dsc{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r);padding:1.2rem;}
.dscv{font-family:var(--fd);font-size:1.9rem;font-weight:800;margin-bottom:0.15rem;}
.dscl{font-size:0.72rem;color:var(--t3);text-transform:uppercase;letter-spacing:0.06em;}
.hl{display:flex;flex-direction:column;gap:0.55rem;}
.hi{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r3);padding:0.8rem 1.1rem;display:flex;justify-content:space-between;align-items:center;gap:0.9rem;}
.hp{font-size:0.85rem;color:var(--t2);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.hscore{font-family:var(--fd);font-size:1rem;font-weight:800;flex-shrink:0;}
.sg{color:var(--ok);}.so{color:var(--gold);}.sb{color:var(--bad);}

/* FOOTER */
footer{border-top:1px solid var(--brd);padding:3.5rem 2rem 1.75rem;}
.fin{max-width:1180px;margin:0 auto;}
.footer-grid{display:grid;grid-template-columns:1.8fr 1fr 1fr 1fr;gap:3rem;margin-bottom:3rem;}
.footer-brand p{font-size:0.82rem;color:var(--t3);line-height:1.7;margin-top:0.75rem;max-width:210px;}
.footer-col-title{font-family:var(--fd);font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;color:var(--t2);margin-bottom:1rem;}
.footer-link{display:block;font-size:0.83rem;color:var(--t3);cursor:pointer;margin-bottom:0.5rem;transition:color 0.16s;line-height:1.5;}
.footer-link:hover{color:var(--t1);}
.fbot{display:flex;justify-content:space-between;align-items:center;border-top:1px solid var(--brd);padding-top:1.5rem;flex-wrap:wrap;gap:0.75rem;}
.fc2{font-size:0.76rem;color:var(--t3);}
.footer-powered{display:flex;align-items:center;gap:0.4rem;font-size:0.74rem;color:var(--t3);}

/* NAV USER */
.nav-user{display:flex;align-items:center;gap:0.5rem;padding:0.25rem 0.6rem 0.25rem 0.5rem;background:var(--bg2);border:1px solid var(--brd);border-radius:40px;}
.nav-avatar{font-size:0.9rem;}
.nav-uname{font-size:0.78rem;font-weight:600;color:var(--t1);max-width:80px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.btn-xs{padding:0.18rem 0.55rem;font-size:0.68rem;}
.btn-link{background:none;border:none;color:var(--acc3);cursor:pointer;font-size:0.84rem;padding:0;text-decoration:underline;text-underline-offset:2px;}

/* AUTH PAGE */
.auth-page{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:2rem;background:var(--bg);}
.auth-card{background:var(--sur);border:1px solid var(--brd2);border-radius:var(--r);padding:2.5rem;max-width:400px;width:100%;box-shadow:var(--sh);}
.auth-logo{display:flex;align-items:center;gap:0.6rem;justify-content:center;margin-bottom:2rem;}
.auth-tabs{display:flex;gap:0.5rem;margin-bottom:2rem;}
.auth-tab{flex:1;padding:0.5rem;border:none;background:transparent;color:var(--t3);font-size:0.88rem;font-weight:600;border-bottom:2px solid transparent;cursor:pointer;transition:all 0.18s;}
.auth-tab.act{color:var(--acc3);border-color:var(--acc);}
.auth-field{margin-bottom:1rem;}
.auth-field label{display:block;font-size:0.78rem;font-weight:600;color:var(--t2);margin-bottom:0.35rem;}
.auth-err{background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.2);color:var(--bad);border-radius:var(--r3);padding:0.6rem 0.9rem;font-size:0.82rem;margin-bottom:1rem;}
.auth-note{font-size:0.74rem;color:var(--t3);text-align:center;margin-top:1rem;line-height:1.6;}

/* COACH CHAT */
.coach-chat{display:flex;flex-direction:column;gap:1rem;}
.chat-log{display:flex;flex-direction:column;gap:0.75rem;min-height:180px;background:var(--bg2);border:1px solid var(--brd);border-radius:var(--r);padding:1.25rem;}
.chat-bubble{display:flex;align-items:flex-start;gap:0.6rem;max-width:90%;animation:fadeUp 0.3s ease;}
.coach-bubble{align-self:flex-start;}
.user-bubble{align-self:flex-end;flex-direction:row-reverse;}
.chat-avatar{font-size:1.25rem;flex-shrink:0;margin-top:0.1rem;}
.chat-text{background:var(--sur);border:1px solid var(--brd);border-radius:var(--r2);padding:0.65rem 0.9rem;font-size:0.87rem;color:var(--t1);line-height:1.6;}
.user-bubble .chat-text{background:rgba(124,92,252,0.12);border-color:rgba(124,92,252,0.25);}
.coach-input-row{display:flex;gap:0.65rem;align-items:center;}

/* MISSION LOCKS */
.locked-mc{opacity:0.45;cursor:not-allowed !important;position:relative;}
.locked-overlay{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(8,8,16,0.85);border-radius:var(--r2);padding:0.4rem 0.75rem;font-size:0.75rem;font-weight:700;color:var(--t2);pointer-events:none;white-space:nowrap;}

/* MISC */
.nf{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:calc(100vh - 60px);text-align:center;gap:1rem;padding:2rem;}
.nfc{font-family:var(--fd);font-size:6rem;font-weight:800;background:linear-gradient(135deg,var(--acc2),var(--neon));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.es{text-align:center;padding:4rem 2rem;color:var(--t3);}
.esi{font-size:3rem;margin-bottom:0.9rem;opacity:0.3;}
.es h3{font-family:var(--fd);font-size:1rem;color:var(--t2);margin-bottom:0.35rem;}
.es p{font-size:0.85rem;}

/* TOAST */
.toast{position:fixed;bottom:1.5rem;right:1.5rem;background:var(--sur2);border:1px solid var(--brd2);border-radius:var(--r2);padding:0.65rem 1.1rem;font-size:0.85rem;z-index:9999;animation:fadeUp 0.3s ease;box-shadow:var(--sh);max-width:300px;pointer-events:none;}
.toast.ok{border-color:rgba(74,222,128,0.3);color:var(--ok);}
.toast.err{border-color:rgba(248,113,113,0.3);color:var(--bad);}
.toast.xp{border-color:rgba(245,158,11,0.3);color:var(--gold);}

/* ANIMATIONS */
@keyframes spin{to{transform:rotate(360deg);}}
@keyframes fadeUp{from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);}}
@keyframes scorePop{0%{transform:scale(0.5);opacity:0;}60%{transform:scale(1.1);}100%{transform:scale(1);opacity:1;}}
@keyframes xpPop{0%{transform:scale(1);}40%{transform:scale(1.18);}100%{transform:scale(1);}}
@keyframes pulseMic{0%,100%{box-shadow:0 0 0 0 rgba(244,63,94,0.4);}50%{box-shadow:0 0 0 7px rgba(244,63,94,0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
.fade-in{animation:fadeIn 0.4s ease;}
.fade-up{animation:fadeUp 0.5s ease both;}

/* RESPONSIVE */
@media(max-width:1024px){
  .mm-grid{grid-template-columns:repeat(2,1fr);}
  .why-inner{grid-template-columns:1fr;gap:2.5rem;}
  .testi-grid{grid-template-columns:repeat(2,1fr);}
}
@media(max-width:900px){
  .nav-links{display:none;}
  .footer-grid{grid-template-columns:1fr 1fr;}
  .proof-strip{grid-template-columns:repeat(2,1fr);}
}
@media(max-width:768px){
  .rgrid{grid-template-columns:1fr;}
  .pv-row{grid-template-columns:1fr;}
  .mm-grid{grid-template-columns:1fr;}
  .ba-grid{grid-template-columns:1fr;}
  .testi-grid{grid-template-columns:1fr;}
  .hero h1{font-size:2.6rem;}
  .hstats{gap:2rem;}
}
@media(max-width:480px){
  .nav{padding:0 1rem;}
  .mgrid{grid-template-columns:1fr;}
  .badges-grid{grid-template-columns:1fr 1fr;}
  .footer-grid{grid-template-columns:1fr;}
  .proof-strip{grid-template-columns:1fr 1fr;}
  .xp-status .xp-info{display:none;}
}
`;

// ─────────────────────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────────────────────
function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return <div className={`toast ${type}`}>{message}</div>;
}

// ─────────────────────────────────────────────────────────────
// XP STATUS BAR
// ─────────────────────────────────────────────────────────────
function XPPill({ setPage }) {
  const { xp, level, xpToNext } = useApp();
  const [pop, setPop] = useState(false);
  const prev = useRef(xp);
  useEffect(() => {
    if (xp !== prev.current) { prev.current = xp; setPop(true); setTimeout(() => setPop(false), 400); }
  }, [xp]);
  const pct = Math.round(((200 - xpToNext) / 200) * 100);
  return (
    <div className={`xp-status ${pop ? "pop" : ""}`} onClick={() => setPage("dashboard")} title="View Dashboard">
      <div className="xp-avatar">⚡</div>
      <div className="xp-info">
        <div className="xp-top">
          <span className="xp-num">{xp} XP</span>
          <span className="xp-sep">·</span>
          <span className="xp-lv">Lv.{level}</span>
        </div>
        <div className="xp-bar-wrap">
          <div className="xp-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// NAVBAR
// ─────────────────────────────────────────────────────────────
function NavBar({ page, setPage, logout, user, onAuthNav }) {
  const mainLinks = [
    { id:"landing",    label:"Home" },
    { id:"analyzer",   label:"Analyzer" },
    { id:"coach",      label:"Coach" },
    { id:"missions",   label:"Missions" },
    { id:"playground", label:"Playground" },
  ];
  const moreLinks = [
    { id:"leaderboard", label:"Leaderboard" },
    { id:"dashboard",   label:"Dashboard" },
    { id:"pricing",     label:"Pricing" },
  ];
  return (
    <nav className="nav">
      <div className="nav-logo" onClick={() => setPage("landing")}>
        <div className="logo-icon">⚒</div>
        <span className="logo-txt">PromptForge</span>
      </div>
      <div className="nav-links">
        {mainLinks.map(i => (
          <button key={i.id} className={`nl ${page === i.id ? "act" : ""}`} onClick={() => setPage(i.id)}>{i.label}</button>
        ))}
        <div className="nav-divider" />
        {moreLinks.map(i => (
          <button key={i.id} className={`nl ${page === i.id ? "act" : ""}`} onClick={() => setPage(i.id)}>{i.label}</button>
        ))}
      </div>
      <div className="nav-r">
        {user ? (
          <>
            <XPPill setPage={setPage} />
            <div className="nav-user" title={`Logged in as ${user.username}`}>
              <span className="nav-avatar">👤</span>
              <span className="nav-uname">{user.username}</span>
              <button className="btn btn-ghost btn-xs" onClick={logout}>Sign out</button>
            </div>
          </>
        ) : (
          <div style={{ display:"flex",gap:"0.5rem",alignItems:"center" }}>
            <button className="btn btn-ghost btn-sm" onClick={onAuthNav}>Sign in</button>
            <button className="btn btn-neon btn-sm" onClick={onAuthNav}>Get Started →</button>
          </div>
        )}
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────
// METER
// ─────────────────────────────────────────────────────────────
function Meter({ label, value }) {
  const cls = value >= 70 ? "mf-ok" : value < 40 ? "mf-bad" : "";
  return (
    <div className="meter">
      <div className="mh">
        <span className="ml">{label}</span>
        <span className="mv" style={{ color: value >= 70 ? "var(--ok)" : value < 40 ? "var(--bad)" : "var(--gold)" }}>{value ?? "—"}</span>
      </div>
      <div className="mt"><div className={`mf ${cls}`} style={{ width: `${Math.min(value||0,100)}%` }} /></div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARE MODAL
// ─────────────────────────────────────────────────────────────
function ShareModal({ data, onClose, onShare }) {
  const text = `I improved my AI prompt score from ${data.originalScore} → ${data.newScore} using PromptForge! 🚀\n\nOptimized with PromptForge — promptforge.ai`;
  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => onShare());
    onClose();
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal fade-up" onClick={e => e.stopPropagation()}>
        <div style={{ marginBottom:"1.25rem" }}>
          <div className="slabel">Share Your Win</div>
          <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.3rem" }}>Show off your improvement</div>
        </div>
        <div className="share-card">
          <div style={{ fontSize:"0.7rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:"0.75rem" }}>Prompt Score Improvement</div>
          <div className="share-scores">
            <div>
              <div className="share-score-num" style={{ color:"var(--bad)" }}>{data.originalScore}</div>
              <div style={{ fontSize:"0.68rem",color:"var(--t3)" }}>Original</div>
            </div>
            <div className="share-arrow">→</div>
            <div>
              <div className="share-score-num" style={{ color:"var(--neon)" }}>{data.newScore}</div>
              <div style={{ fontSize:"0.68rem",color:"var(--t3)" }}>Improved</div>
            </div>
          </div>
          <div style={{ fontSize:"0.8rem",color:"var(--t2)",fontFamily:"monospace",background:"var(--bg2)",padding:"0.75rem",borderRadius:"var(--r3)",textAlign:"left",wordBreak:"break-word" }}>
            {data.improvedPrompt?.slice(0,160)}{data.improvedPrompt?.length > 160 ? "..." : ""}
          </div>
          <div className="share-watermark">⚒ Optimized with PromptForge — promptforge.ai</div>
        </div>
        <div style={{ display:"flex",gap:"0.75rem",marginTop:"1.25rem" }}>
          <button className="btn btn-neon" style={{ flex:1 }} onClick={copy}>📋 Copy to Clipboard</button>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LANDING PAGE
// ─────────────────────────────────────────────────────────────
const DEMO_PROMPT = "Write a blog post about AI.";
const SAMPLE_BEFORE = { score:28, clarity:20, context:15, specificity:18, outputInstructions:10, structure:22, prompt:"Write a blog post about AI." };
const SAMPLE_AFTER  = { score:87, clarity:90, context:85, specificity:88, outputInstructions:82, structure:91, prompt:"You are a senior technology journalist writing for a tech-savvy audience aged 25–40. Write a 1,200-word blog post titled 'How AI is Quietly Reshaping Everyday Work in 2025'. Structure it with: an attention-grabbing intro, 3 main sections with real-world examples, and a forward-looking conclusion. Use a conversational but authoritative tone. Output in Markdown with H2 subheadings." };

function SampleModal({ onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal fade-up" style={{ maxWidth:680 }} onClick={e => e.stopPropagation()}>
        <div style={{ marginBottom:"1.25rem" }}>
          <div className="slabel">Live Example</div>
          <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.2rem" }}>Before vs After — Same Prompt, Massive Difference</div>
        </div>
        <div className="ba-grid">
          <div className="ba-card">
            <div className="ba-lbl" style={{ color:"var(--bad)" }}>❌ Before — Score: {SAMPLE_BEFORE.score}/100</div>
            <div className="ba-prompt">"{SAMPLE_BEFORE.prompt}"</div>
            <div className="ba-scores">
              {[["Clarity",SAMPLE_BEFORE.clarity],["Context",SAMPLE_BEFORE.context],["Specificity",SAMPLE_BEFORE.specificity],["Output Instructions",SAMPLE_BEFORE.outputInstructions],["Structure",SAMPLE_BEFORE.structure]].map(([l,v])=>(
                <div key={l} className="meter">
                  <div className="mh"><span className="ml">{l}</span><span className="mv" style={{color:"var(--bad)"}}>{v}</span></div>
                  <div className="mt"><div className="mf mf-bad" style={{width:`${v}%`}}/></div>
                </div>
              ))}
            </div>
          </div>
          <div className="ba-card after">
            <div className="ba-lbl" style={{ color:"var(--neon)" }}>✅ After — Score: {SAMPLE_AFTER.score}/100</div>
            <div className="ba-prompt" style={{ color:"var(--neon)",fontSize:"0.78rem" }}>"{SAMPLE_AFTER.prompt}"</div>
            <div className="ba-scores">
              {[["Clarity",SAMPLE_AFTER.clarity],["Context",SAMPLE_AFTER.context],["Specificity",SAMPLE_AFTER.specificity],["Output Instructions",SAMPLE_AFTER.outputInstructions],["Structure",SAMPLE_AFTER.structure]].map(([l,v])=>(
                <div key={l} className="meter">
                  <div className="mh"><span className="ml">{l}</span><span className="mv" style={{color:"var(--ok)"}}>{v}</span></div>
                  <div className="mt"><div className="mf mf-ok" style={{width:`${v}%`}}/></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display:"flex",justifyContent:"flex-end",marginTop:"1.25rem",gap:"0.75rem" }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function LandingPage({ setPage }) {
  const [sampleOpen, setSampleOpen] = useState(false);

  const features = [
    {
      icon:"🔬", title:"Prompt Analyzer", page:"analyzer",
      meta:"Instant · No API needed",
      desc:"Paste any prompt and see a score across 5 dimensions — Role, Context, Format, Constraints, and Clarity — with a rewritten improved version.",
      img:"https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=600&q=80&auto=format",
    },
    {
      icon:"🤖", title:"AI Coach", page:"coach",
      meta:"Iterative · Up to 5 rounds",
      desc:"Pick a challenge topic, write your prompt, and let the coach give you per-dimension breakdowns with prioritized fixes until you hit 85+.",
      img:"https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=600&q=80&auto=format",
    },
    {
      icon:"🎯", title:"Training Missions", page:"missions",
      meta:"50 missions · 10 levels",
      desc:"Work through leveled challenges from beginner role-setting to expert chain-of-thought and adversarial defense. Three fresh daily missions every 24 hours.",
      img:"https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=600&q=80&auto=format",
    },
    {
      icon:"🧪", title:"Playground", page:"playground",
      meta:"Side-by-side · Instant diff",
      desc:"Compare two prompt variants head-to-head with simulated AI output and a clear score gap explanation. A/B test before you commit.",
      img:"https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=600&q=80&auto=format",
    },
    {
      icon:"🏆", title:"Leaderboard", page:"leaderboard",
      meta:"Global · Real-time XP",
      desc:"Earn XP with every session, unlock 14 unique badges, and compete on the global leaderboard as your skills measurably improve.",
      img:"https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&q=80&auto=format",
    },
    {
      icon:"🛡️", title:"Injection Defense", page:"missions",
      meta:"Expert · Security focused",
      desc:"Train to recognize, neutralize, and build prompts that resist injection attacks — the skill every enterprise AI engineer needs.",
      img:"https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=600&q=80&auto=format",
    },
  ];

  const testimonials = [
    { stars:5, quote:"I went from mediocre outputs to production-ready results. The per-dimension scoring showed exactly what I was missing — in seconds.", name:"Arjun Rao", role:"Senior Engineer, Notion", initials:"AR", color:"linear-gradient(135deg,#7c5cfc,#a78bff)" },
    { stars:5, quote:"The daily missions are genuinely addictive. Three weeks in, barely missed a day. My team noticed my AI-generated code reviews got sharper.", name:"Sofia Fischer", role:"ML Engineer, Linear", initials:"SF", color:"linear-gradient(135deg,#00f0c0,#00c49a)" },
    { stars:5, quote:"The injection defense training alone was worth it for our security team. Nothing else teaches this practically with real scored attack patterns.", name:"Kenji Tanaka", role:"Security Lead, Retool", initials:"KT", color:"linear-gradient(135deg,#f59e0b,#e08010)" },
    { stars:5, quote:"The leaderboard got competitive in our Slack. Everyone signed up to beat each other's scores. Best unplanned team-building we've had.", name:"Lucas Weber", role:"Engineering Manager, Vercel", initials:"LW", color:"linear-gradient(135deg,#38bdf8,#1a8acc)" },
    { stars:5, quote:"The AI coach doesn't just give you the answer — it makes you think. That's what separates PromptForge from tools that just rewrite for you.", name:"Nadia Gupta", role:"AI Researcher, Loom", initials:"NG", color:"linear-gradient(135deg,#e879a8,#cc5585)" },
    { stars:4, quote:"Used to eyeball prompts and hope for the best. Now I have a real framework. My LLM costs dropped because I stopped wasting tokens on vague prompts.", name:"Priya Mehta", role:"Product Manager, Supabase", initials:"PM", color:"linear-gradient(135deg,#ff6b6b,#cc4444)" },
  ];

  return (
    <div>
      {sampleOpen && <SampleModal onClose={() => setSampleOpen(false)} />}

      {/* ── HERO ───────────────────────────────────────────── */}
      <section className="hero">
        <div className="hbg" /><div className="hgrid" />

        <div className="hero-badge-wrap fade-in">
          <span className="hero-badge">
            <span className="hero-badge-dot" />
            AI Prompt Training Simulator · Free to start
          </span>
        </div>

        <h1 className="fade-up">
          <span className="hero-eyeline">Master the Art of</span>
          <span className="hero-eyeline"><span className="grad">Prompt Engineering</span></span>
        </h1>
        <p className="hero-sub fade-up" style={{ animationDelay:"0.08s" }}>
          Train yourself to write prompts that actually work
        </p>
        <p className="hero-desc fade-up" style={{ animationDelay:"0.14s" }}>
          PromptForge scores, coaches, and challenges you until your prompts go from vague guesses to precise, powerful instructions that unlock the full capability of any AI.
        </p>

        <div className="hacts fade-up" style={{ animationDelay:"0.2s" }}>
          <button className="btn btn-neon btn-lg" onClick={() => setPage("analyzer")}>
            Analyze a Prompt →
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setPage("missions")}>
            Try Missions
            <span className="hmission-badge">{MISSIONS.length}</span>
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => setSampleOpen(true)}>
            View Sample Score
          </button>
        </div>

        <div className="hstats fade-up" style={{ animationDelay:"0.3s" }}>
          {[["5","Scoring dimensions"],["50+","Training missions"],["Free","To start"],["AI","Powered coach"]].map(([n,l]) => (
            <div key={l} className="hstat">
              <div className="hsn">{n}</div>
              <div className="hsl">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TRUSTED BY ─────────────────────────────────────── */}
      <div className="trusted-sec">
        <div className="trusted-label">Trusted by teams at</div>
        <div className="trusted-row">
          {["Notion","Linear","Vercel","Supabase","Loom","Retool","Framer"].map(n => (
            <div key={n} className="trusted-name">{n}</div>
          ))}
        </div>
      </div>

      {/* ── SOCIAL PROOF STRIP ─────────────────────────────── */}
      <div className="proof-strip">
        {[
          ["24K+","Prompts analyzed and scored"],
          ["8.2K","Active engineers training daily"],
          ["50+","Structured missions across 10 levels"],
          ["91%","Users improved scores in their first week"],
        ].map(([num,desc]) => (
          <div key={num} className="proof-cell">
            <div className="proof-num">{num}</div>
            <div className="proof-desc">{desc}</div>
          </div>
        ))}
      </div>

      {/* ── HOW IT WORKS ───────────────────────────────────── */}
      <section className="how-sec">
        <div style={{ marginBottom:"0.5rem" }} className="slabel">How it works</div>
        <h2 className="stitle">Built like a skill training game</h2>
        <div className="how-grid">
          {[
            ["01","Write a prompt","Drop any prompt into the Analyzer — it scores you instantly across 5 dimensions with no API call needed."],
            ["02","See what's wrong","Get a precise breakdown: what's missing, what to fix first, and an AI-rewritten improved version to study."],
            ["03","Train with missions","Take on 50 leveled challenges from beginner to master. Daily missions rotate every 24 hours to keep you sharp."],
            ["04","Level up and compete","Earn XP, unlock badges, and climb the global leaderboard as your prompts measurably improve over time."],
          ].map(([num,title,desc]) => (
            <div key={num} className="how-step">
              <div className="how-num">{num}</div>
              <div className="how-title">{title}</div>
              <div className="how-desc">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES MINDMAP ───────────────────────────────── */}
      <section className="fsec">
        <div className="fsec-hdr">
          <div className="slabel">Your training toolkit</div>
          <h2 className="stitle">Six tools, one mission</h2>
          <p style={{ color:"var(--t2)",fontSize:"0.95rem",marginTop:"0.75rem",fontWeight:300,lineHeight:1.7 }}>
            Every feature connects back to the same goal: turning vague, ineffective prompts into precise, powerful ones.
          </p>
        </div>

        {/* Hub */}
        <div className="mm-hub">
          <div className="mm-hub-inner">
            <div className="mm-hub-glow" />
            <div className="mm-hub-title">⚒ PromptForge</div>
            <div className="mm-hub-sub">Your complete prompt training system</div>
          </div>
        </div>

        {/* Node grid */}
        <div className="mm-grid">
          {features.map(f => (
            <div key={f.title} className="mm-node" onClick={() => setPage(f.page)}>
              <div className="mm-img-wrap">
                <img
                  className="mm-img"
                  src={f.img}
                  alt={f.title}
                  loading="lazy"
                  onError={e => { e.target.style.display = "none"; }}
                />
                <div className="mm-img-overlay" />
                <div className="mm-icon-badge">{f.icon}</div>
              </div>
              <div className="mm-node-title">{f.title}</div>
              <div className="mm-node-desc">{f.desc}</div>
              <div className="mm-node-meta">
                <span className="mm-meta-tag">{f.meta}</span>
                <span className="mm-open-arrow">Open →</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY DIFFERENT ──────────────────────────────────── */}
      <section className="why-sec">
        <div className="why-inner">
          <div className="why-img-col">
            <img
              className="why-img"
              src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80&auto=format"
              alt="AI training concept"
              loading="lazy"
            />
            <div className="why-img-badge">
              <div className="why-badge-num">91%</div>
              <div className="why-badge-label">improvement in week one</div>
            </div>
          </div>
          <div className="why-text-col">
            <div className="slabel">Why it's different</div>
            <h2 className="stitle" style={{ marginBottom:"0.35rem" }}>
              Not a prompt generator.<br />A prompt <span className="grad">trainer.</span>
            </h2>
            <p style={{ color:"var(--t2)",fontSize:"0.93rem",lineHeight:1.8,marginBottom:"0.5rem",fontWeight:300 }}>
              Most AI tools just write prompts for you. PromptForge teaches you <em>why</em> some prompts fail and others excel — so you get better results from every AI tool you use, permanently.
            </p>
            <ul className="why-check-list">
              {[
                "Instant scoring with no API call — results in under 100ms",
                "5-dimension breakdown shows exactly where to improve",
                "AI coach gives iterative feedback over up to 5 rounds",
                "50 structured missions across 10 difficulty levels",
                "XP, badges, and a live leaderboard keep you motivated",
                "Prompt injection defense training for enterprise teams",
              ].map((item, i) => (
                <li key={i} className="why-check-item">
                  <div className="why-check-dot">✓</div>
                  <span className="why-check-text">{item}</span>
                </li>
              ))}
            </ul>
            <div style={{ display:"flex",gap:"0.85rem",flexWrap:"wrap" }}>
              <button className="btn btn-neon btn-lg" onClick={() => setPage("analyzer")}>Start Training Free →</button>
              <button className="btn btn-ghost btn-lg" onClick={() => setPage("pricing")}>View Pricing</button>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ───────────────────────────────────── */}
      <section className="testi-sec">
        <div style={{ textAlign:"center",maxWidth:1100,margin:"0 auto" }}>
          <div className="slabel">Testimonials</div>
          <h2 className="stitle">What engineers are saying</h2>
        </div>
        <div className="testi-grid">
          {testimonials.map((t, i) => (
            <div key={i} className="testi-card">
              <div className="testi-stars">{"★".repeat(t.stars)}{"☆".repeat(5-t.stars)}</div>
              <p className="testi-quote">"{t.quote}"</p>
              <div className="testi-author">
                <div className="testi-av" style={{ background:t.color }}>{t.initials}</div>
                <div>
                  <div className="testi-name">{t.name}</div>
                  <div className="testi-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA BAND ───────────────────────────────────────── */}
      <section className="cta-band">
        <div className="cta-band-glow" />
        <h2>
          Ready to forge<br /><span className="grad">better prompts?</span>
        </h2>
        <p>
          Join 8,200+ engineers already training on PromptForge. Your first session takes 5 minutes. No credit card required.
        </p>
        <div className="cta-btns">
          <button className="btn btn-neon btn-lg" onClick={() => setPage("analyzer")}>Start Training Free →</button>
          <button className="btn btn-ghost btn-lg" onClick={() => setPage("missions")}>Explore Missions</button>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROMPT ANALYZER  (Features 1 + 2 + shareable)
// ─────────────────────────────────────────────────────────────
function AnalyzerPage({ setPage, showToast, initialPrompt }) {
  const { addXP, addAnalysis, addHistory, earnBadge, level } = useApp();
  const [prompt, setPrompt]   = useState(() => {
    if (sessionStorage.getItem("pf_demo")) {
      sessionStorage.removeItem("pf_demo");
      return DEMO_PROMPT;
    }
    return initialPrompt || "";
  });
  const [result, setResult]       = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const sessionCount              = useRef(0);

  const onT = useCallback(t => setPrompt(p => p ? p + " " + t : t), []);
  const { listening, voiceError, start, stop } = useVoice(onT);

  const analyze = () => {
    if (!prompt.trim()) return;
    // Local scoring is synchronous — instant result, no loading needed
    const data = analyzePrompt(prompt);
    setResult(data);
    sessionCount.current += 1;

    const xpGain = Math.max(10, Math.round((data.overallScore / 100) * 30) + 10);
    addXP(xpGain);
    addAnalysis(data.overallScore);
    addHistory({ prompt: prompt.slice(0, 100), score: data.overallScore, type: "analyze" });
    showToast(`+${xpGain} XP earned!`, "xp");

    earnBadge(ALL_BADGES.find(b => b.id === "first"));
    if (data.overallScore >= 80)      earnBadge(ALL_BADGES.find(b => b.id === "scorer80"));
    if (data.overallScore >= 90)      earnBadge(ALL_BADGES.find(b => b.id === "scorer90"));
    if (sessionCount.current >= 5)    earnBadge(ALL_BADGES.find(b => b.id === "streak"));
    if (level >= 5)                   earnBadge(ALL_BADGES.find(b => b.id === "level5"));
  };

  const sc = v => v >= 70 ? "var(--ok)" : v >= 40 ? "var(--gold)" : "var(--bad)";

  return (
    <div className="apage fade-in">
      {shareOpen && result && (
        <ShareModal
          data={{ originalScore: result.overallScore, newScore: Math.min(result.overallScore + 22, 100), improvedPrompt: result.improvedPrompt }}
          onClose={() => setShareOpen(false)}
          onShare={() => { earnBadge(ALL_BADGES.find(b => b.id === "share")); showToast("Copied! Share it 🚀", "ok"); }}
        />
      )}

      <div style={{ marginBottom:"1.75rem" }}>
        <div className="slabel">AI-Powered</div>
        <h1 style={{ fontFamily:"var(--fd)",fontSize:"2rem",fontWeight:800,marginBottom:"0.4rem" }}>Prompt Analyzer</h1>
        <p style={{ color:"var(--t2)",fontSize:"0.9rem" }}>Get brutally honest feedback scored across 5 key dimensions, plus an AI-rewritten improved version.</p>
      </div>

      <div className="piw">
        <textarea
          className="pta"
          placeholder="Paste or speak your prompt here… e.g. 'Write me a blog post about AI'"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => e.key === "Enter" && e.ctrlKey && analyze()}
        />
        <button className={`mic-btn ${listening ? "on" : ""}`} onClick={listening ? stop : start} title="Voice input">
          {listening ? "🔴" : "🎙️"}
        </button>
      </div>
      {voiceError && <div className="verr">🎙️ {voiceError}</div>}

      <div style={{ display:"flex",gap:"0.75rem",marginTop:"0.75rem",flexWrap:"wrap",alignItems:"center" }}>
        <button className="btn btn-neon btn-lg" onClick={analyze} disabled={!prompt.trim()}>
          🔬 Analyze Prompt
        </button>
        {result && (
          <button className="btn btn-ghost btn-sm" onClick={() => setShareOpen(true)}>🔗 Share Result</button>
        )}
        <span style={{ fontSize:"0.78rem",color:"var(--t3)",marginLeft:"auto" }}>Ctrl+Enter to analyze</span>
      </div>



      {result && (
        <div className="rgrid fade-in" style={{ marginTop:"2rem" }}>
          {/* LEFT COLUMN */}
          <div className="sp-col">
            <div className="osc">
              <div className="osn">{result.overallScore}</div>
              <div className="osl">Overall Score</div>
              <div style={{ marginTop:"0.65rem" }}>
                <span className="tag" style={{ background:`${sc(result.overallScore)}18`, color:sc(result.overallScore), border:`1px solid ${sc(result.overallScore)}35` }}>
                  {result.overallScore >= 70 ? "Good" : result.overallScore >= 40 ? "Needs Work" : "Poor"}
                </span>
              </div>
            </div>
            <div className="ssc">
              <Meter label="Clarity"             value={result.clarity} />
              <Meter label="Context"             value={result.context} />
              <Meter label="Structure"           value={result.structure} />
              <Meter label="Specificity"         value={result.specificity} />
              <Meter label="Output Instructions" value={result.outputInstructions} />
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="fpan">
            <div className="fcard">
              <h3>💬 Feedback</h3>
              <p className="ftxt">{result.explanation}</p>
            </div>

            {result.problems?.length > 0 && (
              <div className="fcard">
                <h3>⚠️ Problems Detected</h3>
                <ul className="wl">
                  {result.problems.map((w, i) => (
                    <li key={i} className="wi"><div className="wd" />{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.suggestions?.length > 0 && (
              <div className="fcard">
                <h3>💡 Suggestions</h3>
                <ul className="wl">
                  {result.suggestions.map((s, i) => (
                    <li key={i} className="wi"><div className="wd" style={{ background:"var(--neon)" }} />{s}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="fcard">
              <h3>✅ Improved Prompt</h3>
              <div className="imp">{result.improvedPrompt}</div>
              <button className="btn btn-ghost btn-sm" style={{ marginTop:"0.75rem" }}
                onClick={() => { navigator.clipboard?.writeText(result.improvedPrompt); showToast("Copied!", "ok"); }}>
                📋 Copy Improved Prompt
              </button>
            </div>

            {result.coachQuestions?.length > 0 && (
              <div className="fcard" style={{ borderColor:"rgba(124,92,252,0.2)" }}>
                <h3>🤖 Want to go deeper?</h3>
                <p className="ftxt" style={{ marginBottom:"0.75rem" }}>Your AI Coach has questions to refine this further:</p>
                <ul className="wl">
                  {result.coachQuestions.map((q, i) => (
                    <li key={i} className="wi"><div className="wd" style={{ background:"var(--acc)" }} />{q}</li>
                  ))}
                </ul>
                <button className="btn btn-primary btn-sm" style={{ marginTop:"0.9rem" }}
                  onClick={() => setPage("coach")}>
                  Open AI Coach →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROMPT COACH — Iterative skill trainer.
// USER writes the prompt. Coach gives detailed feedback per round.
// Coach NEVER writes the prompt. Goal: hit 85+ by improving yourself.
// ─────────────────────────────────────────────────────────────

async function getCoachFeedback(userPrompt, attemptNum, topic) {
  const p = `You are a strict, direct prompt engineering coach. Your ONLY job is giving feedback so the user can improve their own prompt. DO NOT rewrite their prompt. DO NOT suggest a complete replacement.

Return ONLY valid JSON:
{
  "score": 68,
  "verdict": "One honest sentence summarizing this attempt.",
  "dimensions": {
    "role":        { "score": 40, "issue": "Missing — there is no persona or role assignment. Start with You are a [role].", "fixed": false },
    "context":     { "score": 55, "issue": "Vague background. Add: who this is for and what situation they are in.", "fixed": false },
    "format":      { "score": 70, "issue": "You said list but did not specify numbered vs bullet.", "fixed": true },
    "constraints": { "score": 30, "issue": "No length, tone, or style rules at all.", "fixed": false },
    "specificity": { "score": 60, "issue": "The task is clear but needs one more concrete detail.", "fixed": false }
  },
  "whatWorked": ["specific praise referencing their actual words"],
  "topFix": "The single most important thing to add or change — be very specific, e.g. add 'for a non-technical audience' after your opening line",
  "secondaryFix": "Second thing to fix once topFix is done",
  "thirdFix": "Optional third thing — only if score below 60, else null",
  "encouragement": "Short, real, gen-z style. No cringe corporate hype."
}

Topic: "${topic}"
Attempt #${attemptNum}: "${userPrompt.slice(0,500)}"

Rules: reference their actual words. Be specific about WHERE to add things. Score harshly — 85 should feel earned.`;
  return callGemini(p, 700);
}

const COACH_TOPICS = [
  { id:"t1", icon:"✉️",  label:"Professional Email",      desc:"Get AI to write a polished follow-up after a job interview" },
  { id:"t2", icon:"💻",  label:"Code Review",             desc:"Get detailed feedback on a specific piece of code" },
  { id:"t3", icon:"📚",  label:"Teach Me Something",      desc:"Get AI to explain a technical concept to a beginner" },
  { id:"t4", icon:"📊",  label:"Data Analysis",           desc:"Get AI to analyze numbers and produce structured insights" },
  { id:"t5", icon:"✍️",  label:"Creative Writing",        desc:"Get a short story with specific constraints and style" },
  { id:"t6", icon:"🤖",  label:"Customer Support Bot",    desc:"Design a system prompt for a helpful support agent" },
  { id:"t7", icon:"🔍",  label:"Research Summary",        desc:"Get a structured summary of a complex topic with sources" },
  { id:"t8", icon:"🎯",  label:"Product Description",     desc:"Write ad copy for a product targeting a specific audience" },
];

const DIM_LABELS = {
  role:        { label:"Role / Persona", tip:"Start with 'You are a [specific expert]…'" },
  context:     { label:"Context",        tip:"Who is this for? What is the situation?" },
  format:      { label:"Output Format",  tip:"Bullet list? JSON? Numbered steps? Tables?" },
  constraints: { label:"Constraints",    tip:"Tone, length, what to avoid, style rules" },
  specificity: { label:"Specificity",    tip:"Concrete details, not vague instructions" },
};

function DimBar({ dimKey, data }) {
  const meta  = DIM_LABELS[dimKey];
  const score = data?.score ?? 0;
  const color = score >= 75 ? "var(--ok)" : score >= 50 ? "var(--gold)" : "var(--bad)";
  return (
    <div style={{ marginBottom:"0.85rem" }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.3rem" }}>
        <span style={{ fontSize:"0.78rem",fontWeight:700,color:"var(--t2)" }}>
          {data?.fixed ? "✅" : "⚠️"} {meta.label}
        </span>
        <span style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"0.85rem",color }}>{score}</span>
      </div>
      <div style={{ height:5,background:"var(--bg3)",borderRadius:3,overflow:"hidden",marginBottom:"0.3rem" }}>
        <div style={{ height:"100%",width:`${score}%`,background:color,borderRadius:3,transition:"width 0.6s ease" }} />
      </div>
      {!data?.fixed && data?.issue && (
        <div style={{ fontSize:"0.74rem",color:"var(--t3)",lineHeight:1.5 }}>→ {data.issue}</div>
      )}
    </div>
  );
}

function CoachPage({ showToast }) {
  const { addXP, addAnalysis, earnBadge } = useApp();
  const [stage,      setStage]     = useState("intro");
  const [topic,      setTopic]     = useState(null);
  const [customText, setCustomText]= useState("");
  const [attempt,    setAttempt]   = useState("");
  const [attemptNum, setAttemptNum]= useState(1);
  const [rounds,     setRounds]    = useState([]); // [{text,feedback,score}]
  const [feedback,   setFeedback]  = useState(null);
  const [loading,    setLoading]   = useState(false);
  const [error,      setError]     = useState("");

  const activeTopic = topic?.id === "custom" ? customText : topic?.desc || "";
  const scoreColor  = s => s >= 75 ? "var(--ok)" : s >= 50 ? "var(--gold)" : "var(--bad)";
  const MAX_ROUNDS  = 5;

  const startSession = (t) => {
    setTopic(t); setAttempt(""); setAttemptNum(1);
    setRounds([]); setFeedback(null); setError(""); setStage("writing");
  };

  const submitAttempt = async () => {
    if (!attempt.trim() || !activeTopic) return;
    setLoading(true); setError("");
    try {
      const fb    = await getCoachFeedback(attempt, attemptNum, activeTopic);
      const score = typeof fb.score === "number" ? fb.score : scorePromptLocal(attempt).overallScore;
      const round = { text: attempt, feedback: fb, score, num: attemptNum };
      setRounds(prev => [...prev, round]);
      setFeedback(fb);
      addAnalysis(score);

      if (score >= 85) {
        const xpGain = Math.max(25, 90 - (attemptNum - 1) * 15);
        addXP(xpGain);
        earnBadge(ALL_BADGES.find(b => b.id === "coach"));
        showToast(`You nailed it! +${xpGain} XP 🏆`, "xp");
        setStage("passed");
      } else if (attemptNum >= MAX_ROUNDS) {
        const xpGain = 15;
        addXP(xpGain); addAnalysis(score);
        showToast(`+${xpGain} XP for trying 💪`, "xp");
        setStage("maxed");
      } else {
        setAttemptNum(n => n + 1);
        setStage("feedback");
      }
    } catch (e) {
      setError(e.message || "Coach session failed. Is the server running?");
    } finally { setLoading(false); }
  };

  const continueRewriting = () => { setStage("writing"); };
  const reset = () => {
    setStage("intro"); setTopic(null); setCustomText(""); setAttempt("");
    setAttemptNum(1); setRounds([]); setFeedback(null); setError("");
  };

  // Live local score preview
  const live = attempt.trim().length > 8 ? scorePromptLocal(attempt) : null;

  // ── INTRO ─────────────────────────────────────────────────
  if (stage === "intro") return (
    <div className="apage fade-in">
      <div style={{ marginBottom:"1.75rem" }}>
        <div className="slabel">Skill Trainer</div>
        <h1 style={{ fontFamily:"var(--fd)",fontSize:"2rem",fontWeight:800,marginBottom:"0.4rem" }}>Prompt Coach 🏋️</h1>
        <div style={{ color:"var(--t2)",fontSize:"0.93rem",maxWidth:540,lineHeight:1.7 }}>
          The coach <strong style={{color:"var(--t1)"}}>never writes prompts for you</strong> — it makes <em>you</em> better.
          Pick a challenge below. Write your own prompt. Get specific, line-by-line feedback each round.
          Hit <strong style={{color:"var(--neon)"}}>85+</strong> to pass and earn XP.
        </div>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))",gap:"0.75rem",marginBottom:"1.5rem" }}>
        {COACH_TOPICS.map(t => (
          <div key={t.id} className="mc" style={{ cursor:"pointer",padding:"1rem" }} onClick={() => startSession(t)}>
            <div style={{ fontSize:"1.5rem",marginBottom:"0.4rem" }}>{t.icon}</div>
            <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.9rem",marginBottom:"0.3rem" }}>{t.label}</div>
            <div style={{ fontSize:"0.76rem",color:"var(--t3)",lineHeight:1.5 }}>{t.desc}</div>
          </div>
        ))}
      </div>

      <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.78rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.6rem" }}>
        Custom challenge
      </div>
      <div style={{ display:"flex",gap:"0.65rem" }}>
        <input className="inp" style={{ flex:1 }} placeholder="Describe what you want the AI to do…"
          value={customText} onChange={e => setCustomText(e.target.value)}
          onKeyDown={e => e.key === "Enter" && customText.trim() && startSession({id:"custom",icon:"🎯",label:"Custom",desc:customText})} />
        <button className="btn btn-primary" disabled={!customText.trim()}
          onClick={() => startSession({id:"custom",icon:"🎯",label:"Custom",desc:customText})}>
          Start →
        </button>
      </div>
    </div>
  );

  // ── WRITING ───────────────────────────────────────────────
  if (stage === "writing") return (
    <div className="apage fade-in">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom:"1.25rem" }} onClick={reset}>← Topics</button>

      {/* Attempt tracker */}
      <div style={{ display:"flex",alignItems:"center",gap:"0.75rem",marginBottom:"1.25rem",flexWrap:"wrap" }}>
        <span style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.9rem" }}>
          {topic?.icon} {topic?.label}
        </span>
        <div style={{ display:"flex",gap:"0.3rem" }}>
          {Array.from({length:MAX_ROUNDS},(_,i)=>i+1).map(n => (
            <div key={n} style={{ width:24,height:5,borderRadius:3,
              background: n < attemptNum ? "var(--ok)" : n === attemptNum ? "var(--acc)" : "var(--bg3)" }} />
          ))}
        </div>
        <span style={{ fontSize:"0.75rem",color:"var(--t3)" }}>Attempt {attemptNum}/{MAX_ROUNDS} · 85+ to pass</span>
      </div>

      {/* Challenge card */}
      <div className="fcard" style={{ marginBottom:"1rem",borderColor:"rgba(124,92,252,0.2)",padding:"0.9rem 1.1rem" }}>
        <div style={{ fontSize:"0.7rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.2rem" }}>Your challenge</div>
        <div style={{ fontWeight:600,fontSize:"0.92rem" }}>{activeTopic}</div>
      </div>

      {/* Previous round reminder */}
      {feedback && (
        <div className="fcard" style={{ marginBottom:"1rem",borderColor:"rgba(251,146,60,0.25)",background:"rgba(251,146,60,0.03)" }}>
          <div style={{ fontSize:"0.76rem",color:"var(--warn)",fontWeight:700,marginBottom:"0.5rem" }}>📌 Last round said — fix these:</div>
          {[feedback.topFix, feedback.secondaryFix, feedback.thirdFix].filter(Boolean).map((fix,i) => (
            <div key={i} className="wi" style={{ marginBottom:"0.3rem" }}>
              <div className="wd" style={{ background:"var(--warn)" }} />
              <span style={{ fontSize:"0.82rem",color:"var(--t2)" }}>{fix}</span>
            </div>
          ))}
        </div>
      )}

      <textarea className="pta" style={{ minHeight:160 }}
        placeholder={attemptNum === 1
          ? "Write your prompt here. Don't overthink it — the first attempt is supposed to be rough. That's how you learn."
          : "Edit your prompt using the feedback above. Reference what the coach told you to fix…"}
        value={attempt}
        onChange={e => setAttempt(e.target.value)}
        onKeyDown={e => e.key === "Enter" && e.ctrlKey && submitAttempt()}
      />

      {/* Live score bar */}
      {live && (
        <div style={{ display:"flex",gap:"0.75rem",alignItems:"center",flexWrap:"wrap",marginTop:"0.5rem",
          padding:"0.5rem 0.75rem",background:"var(--bg2)",borderRadius:"var(--r2)",fontSize:"0.78rem" }}>
          <span style={{ color:"var(--t3)" }}>Live preview:</span>
          <span style={{ fontFamily:"var(--fd)",fontWeight:800,color:scoreColor(live.overallScore),fontSize:"1rem" }}>
            {live.overallScore}
          </span>
          {[["Role",live.structure],["Context",live.context],["Format",live.outputInstructions],["Rules",live.specificity]].map(([l,v])=>(
            <span key={l} style={{ color: v<40?"var(--bad)":v<70?"var(--gold)":"var(--ok)" }}>{l}: {v}</span>
          ))}
          <span style={{ color:"var(--t3)",fontSize:"0.7rem" }}>· AI score may differ</span>
        </div>
      )}

      {error && <div className="errmsg" style={{ marginTop:"0.75rem" }}>{error}</div>}

      <div style={{ display:"flex",gap:"0.75rem",marginTop:"1rem",alignItems:"center",flexWrap:"wrap" }}>
        <button className="btn btn-neon btn-lg" onClick={submitAttempt} disabled={loading||!attempt.trim()}>
          {loading ? <><div className="sp sp-neon"/> Getting feedback…</> : "Submit for Feedback →"}
        </button>
        <span style={{ fontSize:"0.74rem",color:"var(--t3)" }}>Ctrl+Enter · uses AI</span>
      </div>

      {/* Round history */}
      {rounds.length > 0 && (
        <div style={{ marginTop:"2rem" }}>
          <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.78rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:"0.75rem" }}>
            Your attempts
          </div>
          {rounds.map((r,i)=>(
            <div key={i} style={{ display:"flex",gap:"0.85rem",padding:"0.65rem 0",borderBottom:"1px solid var(--brd)",alignItems:"flex-start" }}>
              <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.3rem",color:scoreColor(r.score),minWidth:40,paddingTop:"0.1rem" }}>{r.score}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:"0.78rem",color:"var(--t3)",marginBottom:"0.2rem" }}>Attempt #{r.num}</div>
                <div style={{ fontSize:"0.82rem",color:"var(--t2)",fontFamily:"monospace",lineHeight:1.55 }}>
                  {r.text.slice(0,110)}{r.text.length>110?"…":""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── FEEDBACK ──────────────────────────────────────────────
  if (stage === "feedback" && feedback) {
    const lastRound = rounds[rounds.length-1];
    return (
      <div className="apage fade-in">
        <button className="btn btn-ghost btn-sm" style={{ marginBottom:"1.25rem" }} onClick={reset}>← Topics</button>

        {/* Score header */}
        <div style={{ display:"flex",gap:"1.25rem",alignItems:"center",marginBottom:"1.75rem",flexWrap:"wrap" }}>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontFamily:"var(--fd)",fontWeight:900,fontSize:"3.5rem",lineHeight:1,color:scoreColor(lastRound.score) }}>
              {lastRound.score}
            </div>
            <div style={{ fontSize:"0.66rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em" }}>
              attempt {attemptNum-1}
            </div>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"1rem",marginBottom:"0.3rem" }}>
              {lastRound.score >= 75 ? "Almost there 👀" : lastRound.score >= 55 ? "Making progress 💪" : "Needs work — but that's the point 🔧"}
            </div>
            <div style={{ fontSize:"0.87rem",color:"var(--t2)",lineHeight:1.6 }}>{feedback.verdict}</div>
          </div>
          {rounds.length >= 2 && (
            <div style={{ textAlign:"center",padding:"0.5rem 1rem",background:"rgba(0,240,192,0.06)",border:"1px solid rgba(0,240,192,0.15)",borderRadius:"var(--r2)" }}>
              <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.1rem",color:"var(--neon)" }}>
                +{lastRound.score - rounds[rounds.length-2].score}
              </div>
              <div style={{ fontSize:"0.65rem",color:"var(--t3)" }}>vs last round</div>
            </div>
          )}
        </div>

        {/* Dimension breakdown */}
        {feedback.dimensions && (
          <div className="fcard" style={{ marginBottom:"1rem" }}>
            <h3 style={{ marginBottom:"1rem" }}>📊 Dimension Breakdown</h3>
            {Object.entries(feedback.dimensions).map(([k,v])=>(
              <DimBar key={k} dimKey={k} data={v} />
            ))}
          </div>
        )}

        {/* What worked */}
        {feedback.whatWorked?.length > 0 && (
          <div className="fcard" style={{ marginBottom:"1rem",borderColor:"rgba(74,222,128,0.2)" }}>
            <h3 style={{ color:"var(--ok)",marginBottom:"0.65rem" }}>✅ What worked</h3>
            {feedback.whatWorked.map((w,i)=>(
              <div key={i} className="wi"><div className="wd" style={{background:"var(--ok)"}}/>{w}</div>
            ))}
          </div>
        )}

        {/* Fixes — prioritized */}
        <div className="fcard" style={{ marginBottom:"1rem",borderColor:"rgba(248,113,113,0.2)" }}>
          <h3 style={{ color:"var(--bad)",marginBottom:"0.75rem" }}>🎯 What to fix next — in order</h3>
          {[
            feedback.topFix       && { label:"Fix first",  text:feedback.topFix,       color:"var(--bad)"  },
            feedback.secondaryFix && { label:"Then fix",   text:feedback.secondaryFix, color:"var(--warn)" },
            feedback.thirdFix     && { label:"Also fix",   text:feedback.thirdFix,     color:"var(--gold)" },
          ].filter(Boolean).map((f,i)=>(
            <div key={i} style={{ display:"flex",gap:"0.75rem",alignItems:"flex-start",marginBottom:"0.75rem" }}>
              <span style={{ fontSize:"0.68rem",fontFamily:"var(--fd)",fontWeight:700,color:f.color,
                background:`${f.color}18`,border:`1px solid ${f.color}30`,borderRadius:4,
                padding:"0.15rem 0.45rem",whiteSpace:"nowrap",marginTop:"0.05rem" }}>
                {f.label}
              </span>
              <span style={{ fontSize:"0.87rem",color:"var(--t1)",lineHeight:1.6 }}>{f.text}</span>
            </div>
          ))}
        </div>

        {feedback.encouragement && (
          <div style={{ fontSize:"0.85rem",color:"var(--acc3)",fontStyle:"italic",margin:"0 0 1.25rem",padding:"0.5rem 0" }}>
            Coach: "{feedback.encouragement}"
          </div>
        )}

        <div style={{ display:"flex",gap:"0.75rem",flexWrap:"wrap" }}>
          <button className="btn btn-neon btn-lg" onClick={continueRewriting}>
            ✍️ Rewrite (Attempt #{attemptNum})
          </button>
          <span style={{ display:"flex",alignItems:"center",fontSize:"0.75rem",color:"var(--t3)" }}>
            {MAX_ROUNDS - attemptNum + 1} attempts left · 85+ to pass
          </span>
        </div>
      </div>
    );
  }

  // ── PASSED ────────────────────────────────────────────────
  if (stage === "passed" || stage === "maxed") {
    const best = rounds.reduce((a,r)=>r.score>a.score?r:a, rounds[0]);
    const passed = stage === "passed";
    return (
      <div className="apage fade-in" style={{ textAlign:"center",paddingTop:"2.5rem" }}>
        <div style={{ fontSize:"3.5rem",marginBottom:"0.75rem" }}>{passed?"🏆":"💪"}</div>
        <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"2rem",marginBottom:"0.5rem" }}>
          {passed ? "You actually cooked 🔥" : "Solid effort. Keep grinding."}
        </div>
        <div style={{ color:"var(--t2)",fontSize:"0.93rem",marginBottom:"1.5rem" }}>
          {passed
            ? `Hit ${best.score}/100 in ${rounds.length} attempt${rounds.length>1?"s":""}. That's real skill growth.`
            : `Best score: ${best.score}/100. You improved ${best.score - rounds[0].score} points across ${rounds.length} rounds.`}
        </div>

        {/* Score progression */}
        <div className="fcard" style={{ maxWidth:480,margin:"0 auto 1.5rem",textAlign:"left" }}>
          <h3 style={{ marginBottom:"0.9rem" }}>📈 Your improvement</h3>
          <div style={{ display:"flex",gap:"0.75rem",alignItems:"flex-end",height:60 }}>
            {rounds.map((r,i)=>(
              <div key={i} style={{ display:"flex",flexDirection:"column",alignItems:"center",gap:"0.25rem",flex:1 }}>
                <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"0.85rem",color:scoreColor(r.score) }}>{r.score}</div>
                <div style={{ width:"100%",background:scoreColor(r.score),borderRadius:"3px 3px 0 0",
                  height:`${(r.score/100)*48}px`,minHeight:4,transition:"height 0.5s ease" }} />
                <div style={{ fontSize:"0.6rem",color:"var(--t3)" }}>#{i+1}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Winning prompt */}
        <div className="fcard" style={{ maxWidth:480,margin:"0 auto 1.5rem",textAlign:"left" }}>
          <div style={{ fontSize:"0.7rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.4rem" }}>
            {passed ? "Your winning prompt" : "Your best attempt"}
          </div>
          <div className="imp">{best.text}</div>
          <button className="btn btn-ghost btn-sm" style={{ marginTop:"0.65rem" }}
            onClick={() => { navigator.clipboard?.writeText(best.text); showToast("Copied! 📋","ok"); }}>
            📋 Copy
          </button>
        </div>

        <div style={{ display:"flex",gap:"0.75rem",justifyContent:"center",flexWrap:"wrap" }}>
          <button className="btn btn-neon btn-lg" onClick={reset}>Try Another Challenge →</button>
        </div>
      </div>
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────
// TRAINING MISSIONS — leveled + daily missions, AI-scored
// ─────────────────────────────────────────────────────────────
function MissionsPage({ showToast }) {
  const { addHistory, earnBadge, missionsDone, completeMission,
          completeDailyMission, isDailyDone, level: userLevel } = useApp();
  const [selected,    setSelected]  = useState(null);
  const [isDaily,     setIsDaily]   = useState(false);
  const [userPrompt,  setUserPrompt]= useState("");
  const [loading,     setLoading]   = useState(false);
  const [result,      setResult]    = useState(null);
  const [error,       setError]     = useState("");
  const [tab,         setTab]       = useState("daily");  // "daily"|"all"|level number
  const [filterLevel, setFilterLevel]= useState(null);

  const todayDailies = getTodaysDailies();
  const completedCount = missionsDone.length;
  const allLevels = [...new Set(MISSIONS.map(m => m.level))].sort((a,b)=>a-b);

  const onT = useCallback(t => setUserPrompt(p => p ? p + " " + t : t), []);
  const { listening, voiceError, start, stop } = useVoice(onT);

  const submit = async () => {
    if (!userPrompt.trim() || !selected) return;
    setLoading(true); setError("");
    try {
      const data = await scoreMission(selected, userPrompt);
      setResult(data);
      if (data.passed) {
        if (isDaily) {
          completeDailyMission(selected.id, data.xpEarned || selected.xp);
          showToast(`Daily done! +${data.xpEarned || selected.xp} XP 🔥`, "xp");
        } else {
          completeMission(selected.id, data.xpEarned || selected.xp);
          showToast(`Mission passed! +${data.xpEarned || selected.xp} XP 🎉`, "xp");
        }
        addHistory({ prompt: userPrompt.slice(0, 100), score: data.score, type: "mission", mission: selected.title });
        if (missionsDone.length + 1 >= 3)  earnBadge(ALL_BADGES.find(b => b.id === "mission3"));
        if (missionsDone.length + 1 >= 10) earnBadge(ALL_BADGES.find(b => b.id === "mission10"));
        if (missionsDone.length + 1 >= MISSIONS.length) earnBadge(ALL_BADGES.find(b => b.id === "allMissions"));
      }
    } catch (e) {
      setError(e.message || "Scoring failed. Try again.");
    } finally { setLoading(false); }
  };

  const diffColor = d =>
    d === "Beginner"     ? "var(--ok)" :
    d === "Intermediate" ? "var(--gold)" :
    d === "Advanced"     ? "var(--warn)" :
    d === "Expert"       ? "var(--rose)" : "var(--acc2)";

  const levelColor = l =>
    l <= 2 ? "var(--ok)" : l <= 4 ? "var(--sky)" : l <= 6 ? "var(--gold)" :
    l <= 8 ? "var(--warn)" : l <= 9 ? "var(--rose)" : "var(--acc2)";

  // ── Mission detail view ──────────────────────────────────
  if (selected) return (
    <div className="mpage fade-in">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom:"1.5rem" }}
        onClick={() => { setSelected(null); setResult(null); setUserPrompt(""); }}>← Back to Missions</button>

      {isDaily && (
        <div style={{ display:"inline-flex",alignItems:"center",gap:"0.5rem",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:"var(--r2)",padding:"0.35rem 0.85rem",marginBottom:"1rem",fontSize:"0.8rem",color:"var(--gold)",fontWeight:700 }}>
          ☀️ Daily Mission · Resets Tomorrow
        </div>
      )}

      <div className="card" style={{ marginBottom:"1.25rem" }}>
        <div style={{ display:"flex",gap:"1rem",alignItems:"flex-start" }}>
          <span style={{ fontSize:"2.2rem" }}>{selected.icon}</span>
          <div style={{ flex:1 }}>
            {selected.level && <div style={{ fontSize:"0.7rem",color:levelColor(selected.level),fontWeight:700,fontFamily:"var(--fd)",marginBottom:"0.3rem" }}>LEVEL {selected.level}</div>}
            <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.2rem",marginBottom:"0.35rem" }}>{selected.title}</div>
            <div style={{ color:"var(--t2)",fontSize:"0.88rem",lineHeight:1.6 }}>{selected.goal}</div>
            <div style={{ display:"flex",gap:"0.5rem",marginTop:"0.65rem",flexWrap:"wrap" }}>
              {selected.diff && <span className="tag" style={{ color:diffColor(selected.diff), background:`${diffColor(selected.diff)}18`, border:`1px solid ${diffColor(selected.diff)}30` }}>{selected.diff}</span>}
              <span className="tag tag-gold">+{selected.xp} XP</span>
              {selected.category && <span className="tag tag-acc">{selected.category}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="piw" style={{ marginBottom:"0.75rem" }}>
        <textarea className="pta" placeholder={`Write your best prompt for: "${selected.goal}"`}
          value={userPrompt} onChange={e => setUserPrompt(e.target.value)} />
        <button className={`mic-btn ${listening?"on":""}`} onClick={listening?stop:start}>{listening?"🔴":"🎙️"}</button>
      </div>
      {voiceError && <div className="verr">{voiceError}</div>}
      {error     && <div className="errmsg" style={{ marginTop:"0.5rem" }}>{error}</div>}

      <button className="btn btn-neon btn-lg" onClick={submit} disabled={loading || !userPrompt.trim()}>
        {loading ? <><div className="sp sp-neon" /> Scoring with AI…</> : "Submit for AI Scoring"}
      </button>
      <div style={{ fontSize:"0.75rem",color:"var(--t3)",marginTop:"0.5rem" }}>AI evaluates your prompt — needs server running</div>

      {result && (
        <div className="mres fade-in">
          <div style={{ display:"flex",gap:"2rem",alignItems:"flex-start",flexWrap:"wrap" }}>
            <div style={{ textAlign:"center" }}>
              <div className="mrscore">{result.score}</div>
              <div style={{ fontSize:"0.68rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Score</div>
              {result.passed
                ? <div className="tag tag-ok" style={{ marginTop:"0.4rem" }}>✓ Passed</div>
                : <div className="tag tag-rose" style={{ marginTop:"0.4rem" }}>✗ Try Again</div>}
            </div>
            <div style={{ flex:1 }}>
              {result.passed && <div className="xpgain">+{result.xpEarned || selected.xp} XP 🎉</div>}
              <p style={{ color:"var(--t2)",fontSize:"0.875rem",lineHeight:1.65,marginTop:"0.4rem" }}>{result.feedback}</p>
            </div>
          </div>
          {result.strengths?.length > 0 && (
            <div style={{ marginTop:"1rem" }}>
              <div style={{ fontSize:"0.72rem",color:"var(--ok)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.4rem" }}>Strengths</div>
              {result.strengths.map((s,i) => <div key={i} className="wi"><div className="wd" style={{ background:"var(--ok)" }} />{s}</div>)}
            </div>
          )}
          {result.improvements?.length > 0 && (
            <div style={{ marginTop:"0.75rem" }}>
              <div style={{ fontSize:"0.72rem",color:"var(--warn)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.4rem" }}>Improvements</div>
              {result.improvements.map((s,i) => <div key={i} className="wi"><div className="wd" style={{ background:"var(--warn)" }} />{s}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── Mission list view ─────────────────────────────────────
  const visibleMissions = filterLevel
    ? MISSIONS.filter(m => m.level === filterLevel)
    : MISSIONS;

  return (
    <div className="mpage fade-in">
      <div style={{ marginBottom:"1.5rem" }}>
        <div className="slabel">Skill Training</div>
        <h1 style={{ fontFamily:"var(--fd)",fontSize:"2rem",fontWeight:800,marginBottom:"0.4rem" }}>Training Missions</h1>
        <p style={{ color:"var(--t2)",fontSize:"0.9rem" }}>Complete real-world challenges to earn XP, level up, and prove your skills. Daily missions reset every day for streak XP.</p>
      </div>

      {/* Tabs: Daily | All | Level 1-10 */}
      <div style={{ display:"flex",gap:"0.4rem",marginBottom:"1.5rem",flexWrap:"wrap" }}>
        {[["daily","☀️ Daily"], ["all","All 50"]].map(([id, label]) => (
          <button key={id}
            className={`btn btn-sm ${tab === id ? "btn-primary" : "btn-ghost"}`}
            onClick={() => { setTab(id); setFilterLevel(null); }}>
            {label}
          </button>
        ))}
        <div style={{ width:"1px",background:"var(--brd)",margin:"0 0.25rem" }} />
        {allLevels.map(l => (
          <button key={l}
            className={`btn btn-sm ${tab === "level" && filterLevel === l ? "btn-primary" : "btn-ghost"}`}
            style={{ color: tab === "level" && filterLevel === l ? "" : levelColor(l) }}
            onClick={() => { setTab("level"); setFilterLevel(l); }}>
            Lv.{l}
          </button>
        ))}
      </div>

      {/* Progress bar */}
      <div style={{ display:"flex",gap:"1rem",alignItems:"center",marginBottom:"1.5rem",flexWrap:"wrap" }}>
        <span style={{ fontSize:"0.85rem",color:"var(--t2)" }}>{completedCount}/{MISSIONS.length} completed</span>
        <div style={{ flex:1,height:"4px",background:"var(--bg3)",borderRadius:"2px",overflow:"hidden" }}>
          <div style={{ height:"100%",background:"linear-gradient(90deg,var(--acc),var(--neon))",
            width:`${(completedCount/MISSIONS.length)*100}%`,transition:"width 0.8s ease",borderRadius:"2px" }} />
        </div>
      </div>

      {/* Daily Missions Panel */}
      {tab === "daily" && (
        <div>
          <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1rem",flexWrap:"wrap",gap:"0.5rem" }}>
            <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.95rem" }}>
              ☀️ Today's Daily Missions
              <span style={{ fontSize:"0.72rem",color:"var(--t3)",fontFamily:"var(--fb)",marginLeft:"0.5rem",fontWeight:400 }}>— resets at midnight</span>
            </div>
            <div style={{ fontSize:"0.8rem",color:"var(--t2)" }}>
              {todayDailies.filter(d => isDailyDone(d.id)).length}/3 done today
            </div>
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:"0.85rem",marginBottom:"2rem" }}>
            {todayDailies.map(d => {
              const done = isDailyDone(d.id);
              return (
                <div key={d.id}
                  className={`mc ${done ? "done" : ""}`}
                  style={{ display:"flex",alignItems:"center",gap:"1rem",padding:"1rem 1.25rem" }}
                  onClick={() => !done && (setSelected(d), setIsDaily(true), setResult(null), setUserPrompt(""))}>
                  {done && <div className="done-check">✓</div>}
                  <span style={{ fontSize:"1.8rem" }}>{d.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.95rem" }}>{d.title}</div>
                    <div style={{ fontSize:"0.82rem",color:"var(--t2)",marginTop:"0.2rem" }}>{d.goal}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div className="xpbadge">+{d.xp} XP</div>
                    {done
                      ? <div style={{ fontSize:"0.7rem",color:"var(--ok)",marginTop:"0.2rem" }}>done ✓</div>
                      : <div style={{ fontSize:"0.7rem",color:"var(--t3)",marginTop:"0.2rem" }}>tap to start</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Also show first few regular missions */}
          <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.95rem",marginBottom:"1rem" }}>
            All Missions
          </div>
          <div className="mgrid">
            {MISSIONS.slice(0, 6).map(m => {
              const done = missionsDone.includes(m.id);
              return (
                <div key={m.id} className={`mc ${done?"done":""}`}
                  onClick={() => { setSelected(m); setIsDaily(false); setResult(null); setUserPrompt(""); }}>
                  {done && <div className="done-check">✓</div>}
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem" }}>
                    <span className="mci" style={{ margin:0 }}>{m.icon}</span>
                    <span style={{ fontSize:"0.65rem",fontFamily:"var(--fd)",fontWeight:700,color:levelColor(m.level) }}>LV.{m.level}</span>
                  </div>
                  <div className="mct">{m.title}</div>
                  <div className="mcg">{m.goal}</div>
                  <div className="mcf">
                    <span className="tag tag-acc">{m.category}</span>
                    <span className="xpbadge">+{m.xp} XP</span>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="btn btn-ghost btn-sm" style={{ marginTop:"1rem" }}
            onClick={() => { setTab("all"); setFilterLevel(null); }}>
            View all 50 missions →
          </button>
        </div>
      )}

      {/* All / Level-filtered missions */}
      {(tab === "all" || tab === "level") && (
        <div className="mgrid">
          {visibleMissions.map(m => {
            const done = missionsDone.includes(m.id);
            const locked = m.level > userLevel + 2; // lock missions more than 2 levels ahead
            return (
              <div key={m.id}
                className={`mc ${done?"done":""} ${locked?"locked-mc":""}`}
                title={locked ? `Reach level ${m.level - 2} to unlock` : ""}
                onClick={() => !locked && (setSelected(m), setIsDaily(false), setResult(null), setUserPrompt(""))}>
                {done && <div className="done-check">✓</div>}
                {locked && <div className="locked-overlay">🔒 Lv.{m.level}</div>}
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem" }}>
                  <span className="mci" style={{ margin:0 }}>{m.icon}</span>
                  <span style={{ fontSize:"0.65rem",fontFamily:"var(--fd)",fontWeight:700,color:levelColor(m.level) }}>LV.{m.level}</span>
                </div>
                <div className="mct">{m.title}</div>
                <div className="mcg">{m.goal}</div>
                <div className="mcf">
                  <span className="tag tag-acc">{m.category}</span>
                  <span className="xpbadge">+{m.xp} XP</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// LEADERBOARD + BADGES  (Feature 5)
// ─────────────────────────────────────────────────────────────
function LeaderboardPage() {
  const { board, xp, level, xpToNext, badges } = useApp();
  const [tab, setTab] = useState("board");

  return (
    <div className="lpage fade-in">
      <div style={{ marginBottom:"2rem" }}>
        <div className="slabel">Compete & Earn</div>
        <h1 style={{ fontFamily:"var(--fd)",fontSize:"2rem",fontWeight:800,marginBottom:"0.4rem" }}>Leaderboard</h1>
        <p style={{ color:"var(--t2)",fontSize:"0.9rem" }}>Climb the global rankings and collect badges.</p>
      </div>

      {/* My stats */}
      <div className="card" style={{ marginBottom:"2rem",background:"rgba(124,92,252,0.07)",borderColor:"rgba(124,92,252,0.25)" }}>
        <div style={{ display:"flex",alignItems:"center",gap:"1.5rem",flexWrap:"wrap" }}>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontFamily:"var(--fd)",fontSize:"2.4rem",fontWeight:800,color:"var(--acc2)",lineHeight:1 }}>{level}</div>
            <div style={{ fontSize:"0.68rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Level</div>
          </div>
          <div style={{ flex:1,minWidth:200 }}>
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:"0.3rem" }}>
              <span style={{ fontSize:"0.8rem",color:"var(--t2)" }}>⚡ {xp} XP</span>
              <span style={{ fontSize:"0.75rem",color:"var(--t3)" }}>{xpToNext} to Lv.{level+1}</span>
            </div>
            <div style={{ height:"6px",background:"var(--bg3)",borderRadius:"3px",overflow:"hidden" }}>
              <div style={{ height:"100%",background:"linear-gradient(90deg,var(--acc),var(--neon))",width:`${((xp%200)/(200))*100}%`,transition:"width 1s ease",borderRadius:"3px" }} />
            </div>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontFamily:"var(--fd)",fontSize:"1.4rem",fontWeight:800,color:"var(--gold)",lineHeight:1 }}>{badges.length}</div>
            <div style={{ fontSize:"0.68rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Badges</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex",gap:"0.5rem",marginBottom:"1.5rem" }}>
        {[["board","🏆 Rankings"],["badges","🎖️ Badges"]].map(([id,label]) => (
          <button key={id} className={`btn ${tab === id ? "btn-primary" : "btn-ghost"} btn-sm`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === "board" && (
        <div>
          <div style={{ display:"grid",gridTemplateColumns:"48px 32px 1fr 80px 72px 60px",gap:"0 0.75rem",padding:"0.4rem 1.2rem",fontSize:"0.65rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:"var(--fd)" }}>
            <span>Rank</span><span></span><span>Name</span><span style={{textAlign:"right"}}>XP</span><span style={{textAlign:"right"}}>Best</span><span style={{textAlign:"right"}}>Missions</span>
          </div>
          {board.map((entry, i) => (
            <div key={entry.name} className={`lb-row ${entry.isMe ? "me" : ""}`}
              style={{ display:"grid",gridTemplateColumns:"48px 32px 1fr 80px 72px 60px",gap:"0 0.75rem",alignItems:"center" }}>
              <div className="lb-rank" style={{width:"auto"}}>
                {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i+1}`}
              </div>
              <div className="lb-badge">{entry.badge}</div>
              <div className="lb-name" style={{display:"flex",alignItems:"center",gap:"0.4rem"}}>
                {entry.name}
                {entry.isMe && <span className="tag tag-acc" style={{fontSize:"0.6rem"}}>You</span>}
                {entry.level && <span style={{fontSize:"0.7rem",color:"var(--t3)"}}>Lv.{entry.level}</span>}
              </div>
              <div className="lb-xp" style={{textAlign:"right"}}>{entry.xp.toLocaleString()}</div>
              <div style={{textAlign:"right",fontSize:"0.82rem",color:"var(--gold)",fontFamily:"var(--fd)",fontWeight:700}}>{entry.bestScore || "—"}</div>
              <div style={{textAlign:"right",fontSize:"0.82rem",color:"var(--acc2)",fontFamily:"var(--fd)",fontWeight:700}}>{entry.missionsCompleted ?? "—"}</div>
            </div>
          ))}
          <div style={{ textAlign:"center",marginTop:"1rem",fontSize:"0.8rem",color:"var(--t3)" }}>
            Earn more XP by analyzing prompts, completing missions, and using the coach.
          </div>
        </div>
      )}

      {tab === "badges" && (
        <div className="badges-grid">
          {ALL_BADGES.map(b => {
            const earned = badges.find(e => e.id === b.id);
            return (
              <div key={b.id} className={`badge-card ${earned ? "earned" : "locked"}`}>
                <div className="badge-icon">{b.icon}</div>
                <div className="badge-name">{b.name}</div>
                <div className="badge-desc">{b.desc}</div>
                {earned && <div style={{ fontSize:"0.65rem",color:"var(--gold)",marginTop:"0.35rem" }}>Earned ✓</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROMPT PLAYGROUND  (Feature 7)
// ─────────────────────────────────────────────────────────────
function PlaygroundPage({ showToast }) {
  const { addXP, earnBadge } = useApp();
  const [versions, setVersions] = useState([{ prompt: "", result: null, loading: false, error: "" }]);
  const [hasUsed, setHasUsed]   = useState(false);

  const addVersion = () => {
    if (versions.length >= 3) return;
    setVersions(prev => [...prev, { prompt: "", result: null, loading: false, error: "" }]);
  };

  const update = (i, field, val) => setVersions(prev => prev.map((v, idx) => idx === i ? { ...v, [field]: val } : v));

  const analyze = async (i) => {
    const v = versions[i];
    if (!v.prompt.trim()) return;
    update(i, "loading", true); update(i, "error", ""); update(i, "result", null);
    try {
      const data = await playgroundAnalyze(v.prompt);
      update(i, "result", data); update(i, "loading", false);
      if (!hasUsed) {
        setHasUsed(true);
        addXP(15);
        earnBadge(ALL_BADGES.find(b => b.id === "playground"));
        showToast("+15 XP — Experimenter badge unlocked! 🧪", "xp");
      }
    } catch (e) {
      update(i, "error", e.message || "Failed."); update(i, "loading", false);
    }
  };

  const sc = v => v >= 70 ? "var(--ok)" : v >= 40 ? "var(--gold)" : "var(--bad)";

  return (
    <div className="gpage2 fade-in">
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:"2rem",flexWrap:"wrap",gap:"1rem" }}>
        <div>
          <div className="slabel">Experiment Freely</div>
          <h1 style={{ fontFamily:"var(--fd)",fontSize:"2rem",fontWeight:800,marginBottom:"0.4rem" }}>Prompt Playground</h1>
          <p style={{ color:"var(--t2)",fontSize:"0.9rem" }}>Test multiple prompt versions side by side. See simulated outputs and compare scores.</p>
        </div>
        {versions.length < 3 && (
          <button className="btn btn-ghost btn-sm" onClick={addVersion}>+ Add Version</button>
        )}
      </div>

      <div className={`pv-row`} style={{ gridTemplateColumns: versions.length === 1 ? "1fr" : versions.length === 2 ? "1fr 1fr" : "1fr 1fr 1fr" }}>
        {versions.map((v, i) => (
          <div key={i} style={{ display:"flex",flexDirection:"column",gap:"0.75rem" }}>
            <div className="pv-card">
              <div className="pv-lbl">Version {i+1}{versions.length > 1 && i > 0 && (
                <button style={{ float:"right",background:"none",border:"none",color:"var(--t3)",cursor:"pointer",fontSize:"0.85rem" }} onClick={() => setVersions(prev => prev.filter((_,idx) => idx !== i))}>✕</button>
              )}</div>
              <div className="piw" style={{ marginBottom:"0.6rem" }}>
                <textarea
                  className="pta"
                  style={{ minHeight:"120px" }}
                  placeholder={`Prompt version ${i+1}…`}
                  value={v.prompt}
                  onChange={e => update(i, "prompt", e.target.value)}
                />
              </div>
              <button className="btn btn-neon btn-sm btn-full" onClick={() => analyze(i)} disabled={v.loading || !v.prompt.trim()}>
                {v.loading ? <><div className="sp sp-neon" /> Analyzing…</> : "Analyze →"}
              </button>
              {v.error && <div className="errmsg" style={{ marginTop:"0.5rem" }}>{v.error}</div>}
            </div>

            {v.result && (
              <div className="pv-card fade-in">
                <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.75rem" }}>
                  <div className="pv-lbl">Results</div>
                  <div className="pv-score" style={{ color:sc(v.result.score),fontSize:"1.6rem" }}>{v.result.score}</div>
                </div>
                <div style={{ marginBottom:"0.75rem" }}>
                  <div style={{ fontSize:"0.68rem",color:"var(--t3)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:"0.35rem" }}>Simulated Output</div>
                  <div className="pv-out">"{v.result.simulatedOutput}"</div>
                </div>
                {v.result.quickFix && (
                  <div style={{ padding:"0.6rem 0.75rem",background:"rgba(0,240,192,0.06)",border:"1px solid rgba(0,240,192,0.15)",borderRadius:"var(--r3)",fontSize:"0.8rem",color:"var(--neon)" }}>
                    💡 {v.result.quickFix}
                  </div>
                )}
                {v.result.weaknesses?.length > 0 && (
                  <div style={{ marginTop:"0.6rem" }}>
                    {v.result.weaknesses.map((w,wi) => (
                      <div key={wi} style={{ fontSize:"0.78rem",color:"var(--t3)",marginTop:"0.2rem" }}>⚠ {w}</div>
                    ))}
                  </div>
                )}
                <button className="btn btn-ghost btn-sm" style={{ marginTop:"0.7rem",width:"100%" }}
                  onClick={() => { navigator.clipboard?.writeText(v.prompt); showToast("Prompt copied!", "ok"); }}>
                  📋 Copy Prompt
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DASHBOARD
// ─────────────────────────────────────────────────────────────
function DashboardPage({ setPage }) {
  const { xp, level, xpToNext, history, badges, missionsDone, analyses, avgScore, bestScore, streak } = useApp();
  const sc = s => s >= 70 ? "sg" : s >= 40 ? "so" : "sb";

  return (
    <div className="dpage fade-in">
      <div style={{ marginBottom:"2rem" }}>
        <div className="slabel">Your Progress</div>
        <h1 style={{ fontFamily:"var(--fd)",fontSize:"2rem",fontWeight:800 }}>Dashboard</h1>
      </div>

      {/* Stats */}
      <div className="dgrid">
        {[
          ["⚡ XP",      xp,                                       "var(--acc2)"],
          ["Level",      level,                                    "var(--neon)"],
          ["Analyses",   analyses,                                 "var(--sky)"],
          ["Avg Score",  avgScore || "—", avgScore >= 70 ? "var(--ok)" : avgScore >= 40 ? "var(--gold)" : "var(--bad)"],
          ["Best Score", bestScore || "—",                        "var(--gold)"],
          ["Missions",   `${missionsDone.length}/${MISSIONS.length}`, "var(--acc3)"],
          ["Badges",     badges.length,                            "var(--rose)"],
          ["🔥 Streak",  streak ? `${streak}d` : "—",             "var(--warn)"],
        ].map(([l, v, c]) => (
          <div key={l} className="dsc">
            <div className="dscv" style={{ color: c }}>{v}</div>
            <div className="dscl">{l}</div>
          </div>
        ))}
      </div>

      {/* XP bar */}
      <div className="card" style={{ marginBottom:"1.75rem" }}>
        <div style={{ display:"flex",justifyContent:"space-between",marginBottom:"0.5rem" }}>
          <span style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"0.88rem" }}>Level {level} Progress</span>
          <span style={{ fontSize:"0.78rem",color:"var(--t3)" }}>{xpToNext} XP to next level</span>
        </div>
        <div style={{ height:"8px",background:"var(--bg3)",borderRadius:"4px",overflow:"hidden" }}>
          <div style={{ height:"100%",background:"linear-gradient(90deg,var(--acc),var(--neon))",width:`${((xp%200)/200)*100}%`,transition:"width 1s ease",borderRadius:"4px" }} />
        </div>
      </div>

      {/* Streak + Daily progress */}
      {streak > 0 && (
        <div className="card" style={{ marginBottom:"1.25rem",display:"flex",alignItems:"center",gap:"1.5rem",background:"rgba(251,146,60,0.05)",borderColor:"rgba(251,146,60,0.2)" }}>
          <div style={{ fontSize:"2.5rem" }}>🔥</div>
          <div>
            <div style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.5rem",color:"var(--warn)",lineHeight:1 }}>{streak} day streak</div>
            <div style={{ fontSize:"0.78rem",color:"var(--t3)",marginTop:"0.2rem" }}>keep it going — complete a mission or analyze a prompt today</div>
          </div>
          {streak >= 7 && <span className="tag tag-gold" style={{ marginLeft:"auto" }}>🗓️ 7-day club</span>}
        </div>
      )}

      {/* History */}
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1.5rem",flexWrap:"wrap" }}>
        <div className="card">
          <h2 style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"1rem",marginBottom:"1rem" }}>📜 Recent Activity</h2>
          {history.length === 0 ? (
            <div className="es"><div className="esi">📋</div><h3>No activity yet</h3><p>Analyze a prompt to get started</p></div>
          ) : (
            <div className="hl">
              {history.slice(0, 10).map(h => (
                <div key={h.id} className="hi">
                  <span style={{ fontSize:"0.7rem",color:"var(--t3)",flexShrink:0 }}>{h.type === "mission" ? "🎯" : "🔬"}</span>
                  <span className="hp">{h.prompt}</span>
                  <span className={`hscore ${sc(h.score)}`}>{h.score}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card">
          <h2 style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"1rem",marginBottom:"1rem" }}>🎖️ Badges Earned</h2>
          {badges.length === 0 ? (
            <div className="es"><div className="esi">🏅</div><h3>No badges yet</h3><p>Complete actions to unlock badges</p></div>
          ) : (
            <div style={{ display:"flex",flexWrap:"wrap",gap:"0.5rem" }}>
              {badges.map(b => (
                <div key={b.id} title={b.desc} style={{ textAlign:"center",padding:"0.6rem",background:"var(--sur2)",borderRadius:"var(--r2)",minWidth:60 }}>
                  <div style={{ fontSize:"1.5rem" }}>{b.icon}</div>
                  <div style={{ fontSize:"0.62rem",color:"var(--t3)",marginTop:"0.2rem" }}>{b.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ display:"flex",gap:"0.75rem",marginTop:"1.75rem",flexWrap:"wrap" }}>
        <button className="btn btn-primary" onClick={() => setPage("analyzer")}>🔬 Analyze a Prompt</button>
        <button className="btn btn-ghost" onClick={() => setPage("missions")}>🎯 Try Missions</button>
        <button className="btn btn-ghost" onClick={() => setPage("leaderboard")}>🏆 Leaderboard</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PRICING
// ─────────────────────────────────────────────────────────────
function PricingPage({ setPage }) {
  const plans = [
    { name:"Free", price:"$0", per:"/month", desc:"Start training today.", col:"var(--t2)",
      features:[[true,"Prompt Analyzer (unlimited)"],[true,"5 scoring dimensions"],[true,"AI Coach (3/day)"],[true,"4 Training Missions"],[true,"Playground (2 versions)"],[false,"All 8 missions"],[false,"Leaderboard (full)"],[false,"Priority support"]] },
    { name:"Pro", price:"$12", per:"/month", desc:"For serious prompt engineers.", col:"var(--acc2)", pop:true,
      features:[[true,"Everything in Free"],[true,"Unlimited AI Coach sessions"],[true,"All 8 Training Missions"],[true,"Playground (3 versions)"],[true,"Full Leaderboard access"],[true,"Shareable results"],[true,"Prompt history (unlimited)"],[true,"Priority support"]] },
    { name:"Team", price:"$49", per:"/month", desc:"For AI teams.", col:"var(--neon)",
      features:[[true,"Everything in Pro"],[true,"5 team seats"],[true,"Team leaderboard"],[true,"Shared prompt library"],[true,"Analytics dashboard"],[true,"Custom missions"],[true,"PDF exports"],[true,"Dedicated support"]] },
  ];
  return (
    <div className="pp fade-in">
      <div style={{ textAlign:"center",marginBottom:"3rem" }}>
        <div className="slabel">Pricing</div>
        <h1 className="stitle">Simple, honest pricing</h1>
        <p style={{ color:"var(--t2)",marginTop:"0.5rem" }}>Start free. Upgrade when you're ready.</p>
      </div>
      <div className="pgrid">
        {plans.map(p => (
          <div key={p.name} className={`pcard ${p.pop ? "pop" : ""}`}>
            {p.pop && <div className="pbadge">Most Popular</div>}
            <div className="pname" style={{ color:p.col }}>{p.name}</div>
            <div className="pprice">{p.price}<span>{p.per}</span></div>
            <div className="pdesc">{p.desc}</div>
            <ul className="pfl">{p.features.map(([ok,ft],i) => (
              <li key={i} className="pfi"><span className={ok?"ck":"cx"}>{ok?"✓":"✗"}</span><span style={ok?{}:{opacity:0.4}}>{ft}</span></li>
            ))}</ul>
            <button className={`btn ${p.pop?"btn-primary":"btn-ghost"} btn-full`} onClick={() => setPage("analyzer")}>
              {p.price === "$0" ? "Start for free" : `Get ${p.name}`}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AUTH PAGE  — login / register, JWT via Node.js server
// ─────────────────────────────────────────────────────────────
// ── Math captcha generator ────────────────────────────────
function makeCaptcha() {
  const ops = [
    { q: (a,b) => `${a} + ${b}`, ans: (a,b) => a+b },
    { q: (a,b) => `${a} × ${b}`, ans: (a,b) => a*b },
    { q: (a,b) => `${a+b} − ${b}`, ans: (a,b) => a+b-b },
  ];
  const a   = Math.floor(Math.random()*9)+1;
  const b   = Math.floor(Math.random()*9)+1;
  const op  = ops[Math.floor(Math.random()*ops.length)];
  return { question: `What is ${op.q(a,b)}?`, answer: String(op.ans(a,b)) };
}

function AuthPage({ onSuccess, setPage }) {
  const { login, sendCode, verifyAndRegister, authLoading, authError, setError } = useAuth();
  const [tab,         setTab]       = useState("register"); // "register" | "login"
  const [stage,       setStage]     = useState("form");     // "form" | "verify"
  const [username,    setUsername]  = useState("");
  const [email,       setEmail]     = useState("");
  const [pass,        setPass]      = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [captcha,     setCaptcha]   = useState(makeCaptcha);
  const [captchaVal,  setCaptchaVal]= useState("");
  const [captchaErr,  setCaptchaErr]= useState(false);
  const [codeInput,   setCodeInput] = useState("");
  const [codeErr,     setCodeErr]   = useState("");
  const [devCode,     setDevCode]   = useState(null); // only shown when server has no SMTP

  const switchTab = t => {
    setTab(t); setError(""); setStage("form");
    setCaptcha(makeCaptcha()); setCaptchaVal(""); setCaptchaErr(false);
    setCodeInput(""); setCodeErr(""); setDevCode(null);
  };

  const checkCaptcha = () => {
    if (captchaVal.trim() !== captcha.answer) {
      setCaptchaErr(true); setCaptcha(makeCaptcha()); setCaptchaVal(""); return false;
    }
    return true;
  };

  // ── Register step 1 — validate + send code ─────────────
  const submitRegister = async () => {
    setError("");
    if (!username.trim())         return setError("Username is required");
    if (!email.includes("@"))    return setError("Enter a valid email address");
    if (pass.length < 6)         return setError("Password must be at least 6 characters");
    if (pass !== confirmPass)    return setError("Passwords don't match");
    if (!checkCaptcha())         return;

    const result = await sendCode(username.trim(), email.trim(), pass);
    if (!result) return; // error shown via authError
    setDevCode(result.dev || null); // show code in UI if no SMTP (dev mode)
    setStage("verify");
  };

  // ── Register step 2 — verify email code ─────────────────
  const submitVerify = async () => {
    setCodeErr("");
    if (codeInput.trim().length < 6) return setCodeErr("Enter the 6-digit code from your email");
    const ok = await verifyAndRegister(email.trim(), codeInput.trim());
    if (ok && onSuccess) setTimeout(onSuccess, 600);
  };

  // ── Login ────────────────────────────────────────────────
  const submitLogin = async () => {
    setError("");
    if (!username.trim() || !pass.trim()) return setError("Fill in all fields");
    if (!checkCaptcha()) return;
    const ok = await login(username.trim(), pass);
    if (ok && onSuccess) onSuccess();
  };

  const onKey = e => {
    if (e.key !== "Enter") return;
    if (stage === "verify") submitVerify();
    else if (tab === "login") submitLogin();
    else submitRegister();
  };

  // ── Verify screen ─────────────────────────────────────────
  if (stage === "verify") return (
    <div className="auth-page">
      <div className="auth-card fade-up">
        <div className="auth-logo">
          <div className="logo-icon">⚒</div>
          <span style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.2rem" }}>PromptForge</span>
        </div>

        <div style={{ textAlign:"center",marginBottom:"1.5rem" }}>
          <div style={{ fontSize:"2.5rem",marginBottom:"0.5rem" }}>📧</div>
          <div style={{ fontFamily:"var(--fd)",fontWeight:700,fontSize:"1.05rem" }}>Check your inbox</div>
          <div style={{ color:"var(--t2)",fontSize:"0.83rem",marginTop:"0.35rem",lineHeight:1.6 }}>
            We sent a 6-digit code to<br /><strong>{email}</strong>
          </div>
        </div>

        {/* Dev mode helper — only shown when server has no SMTP */}
        {devCode && (
          <div style={{ background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.25)",
            borderRadius:"var(--r2)",padding:"0.75rem",marginBottom:"1rem" }}>
            <div style={{ fontSize:"0.72rem",color:"var(--gold)",fontWeight:700,marginBottom:"0.3rem" }}>
              🛠️ Dev mode — no SMTP configured
            </div>
            <div style={{ fontSize:"0.78rem",color:"var(--t2)" }}>Your code: <strong style={{ fontFamily:"monospace",fontSize:"1.1rem",letterSpacing:"0.18em",color:"var(--gold)" }}>{devCode}</strong></div>
            <div style={{ fontSize:"0.68rem",color:"var(--t3)",marginTop:"0.25rem" }}>
              Set SMTP_HOST, SMTP_USER, SMTP_PASS in .env to send real emails
            </div>
          </div>
        )}

        {(authError||codeErr) && <div className="auth-err">⚠️ {authError||codeErr}</div>}

        <div className="auth-field">
          <label>Verification Code</label>
          <input className="inp" placeholder="000000" value={codeInput} maxLength={6}
            onChange={e => setCodeInput(e.target.value.replace(/[^0-9]/g,""))}
            onKeyDown={onKey} autoFocus
            style={{ letterSpacing:"0.22em",fontSize:"1.3rem",textAlign:"center" }} />
        </div>

        <button className="btn btn-neon btn-full" onClick={submitVerify}
          disabled={authLoading || codeInput.length < 6}>
          {authLoading ? <><div className="sp sp-neon" /> Verifying…</> : "Verify & Create Account →"}
        </button>
        <button className="btn btn-ghost btn-full" style={{ marginTop:"0.5rem" }}
          onClick={() => { setStage("form"); setCodeInput(""); setDevCode(null); }}>
          ← Change email
        </button>
      </div>
    </div>
  );

  // ── Main form ─────────────────────────────────────────────
  return (
    <div className="auth-page">
      <div className="auth-card fade-up">
        <div className="auth-logo">
          <div className="logo-icon">⚒</div>
          <span style={{ fontFamily:"var(--fd)",fontWeight:800,fontSize:"1.2rem" }}>PromptForge</span>
        </div>

        <div className="auth-tabs">
          {[["register","Create Account"],["login","Sign In"]].map(([id,label]) => (
            <button key={id} className={`auth-tab ${tab===id?"act":""}`} onClick={() => switchTab(id)}>{label}</button>
          ))}
        </div>

        {authError && <div className="auth-err">⚠️ {authError}</div>}

        <div className="auth-field">
          <label>Username</label>
          <input className="inp" placeholder="e.g. promptwizard99" value={username}
            onChange={e => setUsername(e.target.value)} onKeyDown={onKey} autoFocus />
        </div>

        {tab === "register" && (
          <div className="auth-field">
            <label>Email</label>
            <input className="inp" type="email" placeholder="you@example.com" value={email}
              onChange={e => setEmail(e.target.value)} onKeyDown={onKey} />
          </div>
        )}

        <div className="auth-field">
          <label>Password</label>
          <input className="inp" type="password" placeholder="min. 6 characters" value={pass}
            onChange={e => setPass(e.target.value)} onKeyDown={onKey} />
        </div>

        {tab === "register" && (
          <div className="auth-field">
            <label>Confirm Password</label>
            <input className="inp" type="password" placeholder="same again" value={confirmPass}
              onChange={e => setConfirmPass(e.target.value)} onKeyDown={onKey} />
          </div>
        )}

        {/* Math captcha — no 3rd party dependency */}
        <div className="auth-field">
          <label style={{ display:"flex",alignItems:"center",gap:"0.5rem" }}>
            Quick check: {captcha.question}
            <button type="button" style={{ background:"none",border:"none",color:"var(--t3)",cursor:"pointer",fontSize:"0.75rem",padding:0 }}
              onClick={() => { setCaptcha(makeCaptcha()); setCaptchaVal(""); setCaptchaErr(false); }}>↻ new</button>
          </label>
          <input className="inp" placeholder="Your answer" value={captchaVal}
            onChange={e => { setCaptchaVal(e.target.value); setCaptchaErr(false); }}
            onKeyDown={onKey}
            style={{ borderColor: captchaErr ? "var(--bad)" : "" }} />
          {captchaErr && <div style={{ fontSize:"0.73rem",color:"var(--bad)",marginTop:"0.25rem" }}>Wrong — try again</div>}
        </div>

        <button className="btn btn-neon btn-full" style={{ marginTop:"0.5rem" }}
          onClick={tab === "login" ? submitLogin : submitRegister}
          disabled={authLoading || !username.trim() || !pass.trim() || !captchaVal.trim()}>
          {authLoading
            ? <><div className="sp sp-neon" /> {tab === "login" ? "Signing in…" : "Sending code…"}</>
            : tab === "login" ? "Sign In →" : "Send Verification Code →"}
        </button>

        <div className="auth-note">
          {tab === "register"
            ? <>Already have an account? <button className="btn-link" onClick={() => switchTab("login")}>Sign in</button></>
            : <>New here? <button className="btn-link" onClick={() => switchTab("register")}>Create an account</button></>}
          {setPage && (
            <><br /><button className="btn-link" style={{ fontSize:"0.71rem",color:"var(--t3)" }} onClick={() => setPage("landing")}>← Back to home</button></>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────
function Footer({ setPage }) {
  const cols = [
    { title: "Tools", links: [
      { label:"Prompt Analyzer", page:"analyzer" },
      { label:"AI Coach",        page:"coach" },
      { label:"Missions",        page:"missions" },
      { label:"Playground",      page:"playground" },
    ]},
    { title: "Platform", links: [
      { label:"Leaderboard", page:"leaderboard" },
      { label:"Dashboard",   page:"dashboard" },
      { label:"Pricing",     page:"pricing" },
    ]},
    { title: "Company", links: [
      { label:"Home",  page:"landing" },
    ]},
  ];
  return (
    <footer>
      <div className="fin">
        <div className="footer-grid">
          {/* Brand */}
          <div className="footer-brand">
            <div className="nav-logo" onClick={() => setPage("landing")} style={{ cursor:"pointer" }}>
              <div className="logo-icon">⚒</div>
              <span className="logo-txt">PromptForge</span>
            </div>
            <p>Your AI prompt training simulator. Score, fix, and master prompt engineering — for free.</p>
          </div>
          {/* Sitemap columns */}
          {cols.map(col => (
            <div key={col.title}>
              <div className="footer-col-title">{col.title}</div>
              {col.links.map(link => (
                <span key={link.label} className="footer-link" onClick={() => setPage(link.page)}>{link.label}</span>
              ))}
            </div>
          ))}
        </div>
        <div className="fbot">
          <span className="fc2">© 2026 PromptForge. All rights reserved.</span>
          <div className="footer-powered">
            <span>⚡</span>
            <span>Powered by Groq + Llama 3.3</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────
const PROTECTED_PAGES = new Set(["analyzer","coach","missions","leaderboard","playground","dashboard"]);

function AppContent() {
  const { user, logout } = useAuth();
  const [page,  setPage]  = useState("landing");
  const [toast, setToast] = useState(null);

  const nav = useCallback(p => {
    setPage(p);
    window.scrollTo({ top:0, behavior:"smooth" });
  }, []);

  // If trying to access a protected page while logged out, redirect to auth
  const guardedNav = useCallback(p => {
    if (PROTECTED_PAGES.has(p) && !user) { setPage("auth"); window.scrollTo({top:0}); return; }
    setPage(p);
    window.scrollTo({ top:0, behavior:"smooth" });
  }, [user]);

  // After login, go to dashboard
  const onLoginSuccess = useCallback(() => {
    nav("dashboard");
  }, [nav]);

  const showToast = useCallback((message, type = "ok") => {
    setToast({ message, type });
  }, []);

  const render = () => {
    // If page is protected and no user, show auth inline
    if (PROTECTED_PAGES.has(page) && !user) return <AuthPage onSuccess={onLoginSuccess} setPage={nav} />;
    switch(page) {
      case "landing":     return <LandingPage setPage={guardedNav} />;
      case "auth":        return <AuthPage onSuccess={onLoginSuccess} setPage={nav} />;
      case "analyzer":    return <AnalyzerPage setPage={guardedNav} showToast={showToast} />;
      case "coach":       return <CoachPage showToast={showToast} />;
      case "missions":    return <MissionsPage showToast={showToast} />;
      case "leaderboard": return <LeaderboardPage />;
      case "playground":  return <PlaygroundPage showToast={showToast} />;
      case "dashboard":   return <DashboardPage setPage={guardedNav} />;
      case "pricing":     return <PricingPage setPage={guardedNav} />;
      default:            return (
        <div className="nf">
          <div className="nfc">404</div>
          <h2>Page not found</h2>
          <button className="btn btn-primary" onClick={() => nav("landing")}>Go Home</button>
        </div>
      );
    }
  };

  return (
    <div className="app">
      <style>{S}</style>
      <NavBar page={page} setPage={guardedNav} logout={logout} user={user} onAuthNav={() => nav("auth")} />
      <main className="page">{render()}</main>
      <Footer setPage={guardedNav} />
      {toast && <Toast key={toast.message + Date.now()} {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}

export default function PromptForge() {
  return (
    <AuthProvider>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </AuthProvider>
  );
}
